// src/views/Clients.jsx
import React, { useEffect, useState, useMemo, useRef } from 'react';
import Split from 'react-split';
import './Clients.css';
import { FaPlus, FaRegSave, FaTrash, FaSearch, FaTimes, FaEdit, FaColumns, FaPaperclip, FaUpload, FaFolderOpen, FaSync } from 'react-icons/fa';

const API = import.meta.env.VITE_API_URL;
const DEFAULT_LIMIT = 50;

/* -----------------------------------------------------------
   Helpers / constants
   ----------------------------------------------------------- */
const emptyClient = {
  id: null,
  status: 1,
  deleted: 0,
  name: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  country: '',
  telephone: '',
  contact: '',
  email: '',
  billingtelephone: '',
  billingcontact: '',
  billingemail: '',
  notes: '',
  monthlystoragetype: 0,
  monthlystorageminimum: 0,
  monthlystoragebillingfreq: 1,
  paymentterms: 30,
  storagefeeper: 2,
  // UI-only flag (not yet persisted server-side): auto-generate storage invoicing
  autogenerate_storage: 0,
  eship_shipping_upcharge_percentage: 0,
  shippingbalance: 0,
  shippingfundlimit: 0,
  shippingfundminthreshold: 0,
};

const US_STATES = [
  "Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut","Delaware",
  "Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa","Kansas","Kentucky","Louisiana",
  "Maine","Maryland","Massachusetts","Michigan","Minnesota","Mississippi","Missouri","Montana",
  "Nebraska","Nevada","New Hampshire","New Jersey","New Mexico","New York","North Carolina",
  "North Dakota","Ohio","Oklahoma","Oregon","Pennsylvania","Rhode Island","South Carolina",
  "South Dakota","Tennessee","Texas","Utah","Vermont","Virginia","Washington","West Virginia",
  "Wisconsin","Wyoming"
];

const numberOrNull = (v, def = null) => {
  if (v === '' || v == null) return def;
  const n = Number(v);
  return Number.isNaN(n) ? def : n;
};
  const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
  // Lenient money string validator: allow empty, digits, optional single dot, up to 2 decimals
  const isValidMoneyStr = (s) => /^\d*(?:\.\d{0,2})?$/.test(String(s || ''));
const toFixed2 = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : '';
};
const USD2 = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtMoney = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? USD2.format(n) : '';
};

/* -----------------------------------------------------------
   Component
   ----------------------------------------------------------- */
export default function Clients() {
  /* ---------- list state ---------- */
  const [clients, setClients] = useState([]);
  const getInitialLimit = () => {
    const stored = window.localStorage.getItem('clientsLimit');
    return stored ? parseInt(stored, 10) : DEFAULT_LIMIT;
  };
  const [limit, setLimit] = useState(getInitialLimit());
  const [limitInput, setLimitInput] = useState(String(getInitialLimit()));
  const [meta, setMeta] = useState({ total: 0, limit: getInitialLimit(), offset: 0 });
  const getInitialQ = () => {
    try { return localStorage.getItem('clientsQ') || ''; } catch { return ''; }
  };
  const [q, setQ] = useState(getInitialQ());
  const [loadingList, setLoadingList] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  // De-dupe guard to prevent double fetches (e.g., StrictMode and overlapping effects)
  const lastFetchKeyRef = useRef('');

  // sorting
  const [sortField, setSortField] = useState('name');
  const [sortOrder, setSortOrder] = useState('ASC');

  // Determine admin status from localStorage user
  const isAdmin = (() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      return u?.isadmin === 1 || u?.isadmin === true || String(u?.isadmin) === '1';
    } catch { return false; }
  })();

  // Resizable columns (Inventory-like)
  const CLIENT_COLS = ['actions','status','id','name','contact','email','paymentterms'];
  // Stable defaults to avoid jitter on first paint when no saved widths
  const DEFAULT_CLIENT_COLWIDTHS = {
    actions: 96,
    status: 100,
    id: 72,
    name: 220,
    contact: 180,
    email: 240,
    paymentterms: 100,
  };
  const LS_CLIENT_COLWIDTHS = 'clientsColWidths.v1';
  const getInitialClientColWidths = () => {
    try {
      const raw = localStorage.getItem(LS_CLIENT_COLWIDTHS);
      const j = raw ? JSON.parse(raw) : {};
      // Normalize + clamp any saved values
      if (j && typeof j === 'object' && Object.keys(j).length) {
        const out = {};
        for (const k of CLIENT_COLS) {
          const v = Number(j[k] || 0);
          const min = k === 'actions' ? 36 : 40;
          const max = k === 'actions' ? 160 : 800;
          out[k] = Number.isFinite(v) && v > 0 ? Math.max(min, Math.min(max, v)) : (DEFAULT_CLIENT_COLWIDTHS[k] || min);
        }
        return out;
      }
      // Seed with defaults if nothing saved yet
      return { ...DEFAULT_CLIENT_COLWIDTHS };
    } catch { return {}; }
  };
  const [clientColWidths, setClientColWidths] = useState(getInitialClientColWidths());
  const [clientAutoFit, setClientAutoFit] = useState(false);
  // Auto-measured widths used only when user has not resized/saved
  const [autoClientColWidths, setAutoClientColWidths] = useState({});
  const clientHeaderRef = useRef(null);
  const clientTableRef = useRef(null);
  const clientResizeRef = useRef({ key: null, startX: 0, startW: 0, minW: 40 });
  useEffect(() => { try { localStorage.setItem(LS_CLIENT_COLWIDTHS, JSON.stringify(clientColWidths)); } catch {} }, [clientColWidths]);
  const ensureClientWidthsLocked = () => {
    if (Object.keys(clientColWidths || {}).length === CLIENT_COLS.length) return;
    const row = clientHeaderRef.current; if (!row) return;
    const next = {}; CLIENT_COLS.forEach((k, i) => { const th = row.children?.[i]; if (th) next[k] = th.offsetWidth; });
    setClientColWidths(next);
  };
  const onClientResizeMove = (e) => {
    const { key, startX, startW } = clientResizeRef.current; if (!key) return;
    const baseMin = 40; // match Receipts minimum column width
    const nextW = Math.max(baseMin, startW + (e.clientX - startX));
    setClientColWidths(prev => ({ ...prev, [key]: nextW })); e.preventDefault();
  };
  const onClientResizeUp = () => {
    clientResizeRef.current = { key: null, startX: 0, startW: 0 };
    try { document.body.classList.remove('col-resizing'); } catch {}
    window.removeEventListener('mousemove', onClientResizeMove);
    window.removeEventListener('mouseup', onClientResizeUp);
  };
  const autoFitClientCol = (key) => {
    try {
      const idx = CLIENT_COLS.indexOf(key); if (idx === -1) return;
      let maxW = 0;
      const hdr = clientHeaderRef.current?.children?.[idx];
      if (hdr) {
        // For actions, don’t let header text dominate; body content defines size
        if (key !== 'actions') maxW = Math.max(maxW, Math.ceil(hdr.scrollWidth));
      }
      const tbl = clientTableRef.current; if (tbl?.tBodies?.[0]) {
        const rows = tbl.tBodies[0].rows;
        for (let r = 0; r < rows.length; r++) {
          const cell = rows[r].children[idx]; if (cell) maxW = Math.max(maxW, Math.ceil(cell.scrollWidth));
        }
      }
      // Clamp: general columns up to 480px; actions much smaller cap (to avoid 25% width)
      const cap = key === 'actions' ? 80 : 480;
      const baseMin = key === 'actions' ? 20 : 20;
      const target = Math.max(baseMin, Math.min(cap, maxW + 12));
      setClientColWidths(prev => ({ ...prev, [key]: target }));
    } catch {}
  };
  const measureClientColWidth = (colIdx) => {
    try {
      let maxW = 0;
      const hdr = clientHeaderRef.current?.children?.[colIdx];
      const key = CLIENT_COLS[colIdx];
      if (hdr && key !== 'actions') maxW = Math.max(maxW, Math.ceil(Math.max(hdr.scrollWidth, hdr.offsetWidth)));
      const tbl = clientTableRef.current;
      if (tbl?.tBodies?.[0]) {
        const rows = tbl.tBodies[0].rows;
        for (let r = 0; r < rows.length; r++) {
          const cell = rows[r].children[colIdx];
          if (cell) maxW = Math.max(maxW, Math.ceil(Math.max(cell.scrollWidth, cell.offsetWidth)));
        }
      }
      const cap = key === 'actions' ? 180 : 800;
      const baseMin = key === 'actions' ? 60 : 40;
      return Math.max(baseMin, Math.min(cap, maxW));
    } catch { return 120; }
  };
  const measureContentMinWidth = (key) => {
    try {
      const idx = CLIENT_COLS.indexOf(key); if (idx === -1) return 40;
      let maxW = 0;
      const hdr = clientHeaderRef.current?.children?.[idx]; if (hdr) maxW = Math.max(maxW, Math.ceil(hdr.scrollWidth));
      const tbl = clientTableRef.current; if (tbl?.tBodies?.[0]) {
        const rows = tbl.tBodies[0].rows;
        for (let r = 0; r < rows.length; r++) {
          const cell = rows[r].children[idx]; if (cell) maxW = Math.max(maxW, Math.ceil(cell.scrollWidth));
        }
      }
      return Math.max(40, Math.min(400, maxW + 16));
    } catch { return 40; }
  };

  const startClientResize = (key, e) => {
    ensureClientWidthsLocked();
    const th = e.currentTarget.parentElement;
    clientResizeRef.current = { key, startX: e.clientX, startW: th.offsetWidth };
    try { document.body.classList.add('col-resizing'); } catch {}
    window.addEventListener('mousemove', onClientResizeMove);
    window.addEventListener('mouseup', onClientResizeUp);
    e.preventDefault(); e.stopPropagation();
  };

  // If user has saved widths, lock widths (no auto-fit on each update).
  // Otherwise, auto-measure each column to the minimum width needed to fit content.
  useEffect(() => {
    const haveSaved = Object.keys(clientColWidths || {}).length === CLIENT_COLS.length;
    if (haveSaved) {
      const t = setTimeout(() => {
        ensureClientWidthsLocked();
        setAutoClientColWidths({}); // not needed when user widths are active
      }, 0);
      return () => clearTimeout(t);
    }

    // Auto-measure widths from header + visible rows
    const t = setTimeout(() => {
      try {
        const next = {};
        CLIENT_COLS.forEach((k, idx) => {
          if (idx === CLIENT_COLS.length - 1) return; // last column will flex via CSS
          next[k] = measureMaxContentWidth(k);
        });
        setAutoClientColWidths(next);
      } catch {}
    }, 0);
    return () => clearTimeout(t);
  }, [clients, clientColWidths]);

  // Measure the natural max content width for a column (no artificial cap)
  const measureMaxContentWidth = (key) => {
    try {
      const idx = CLIENT_COLS.indexOf(key); if (idx === -1) return 40;
      let maxW = 0;
      const hdr = clientHeaderRef.current?.children?.[idx]; if (hdr) maxW = Math.max(maxW, Math.ceil(hdr.scrollWidth));
      const tbl = clientTableRef.current; if (tbl?.tBodies?.[0]) {
        const rows = tbl.tBodies[0].rows;
        for (let r = 0; r < rows.length; r++) {
          const cell = rows[r].children[idx]; if (cell) maxW = Math.max(maxW, Math.ceil(cell.scrollWidth));
        }
      }
      return Math.max(40, maxW + 16);
    } catch { return 40; }
  };

  /* ---------- selection + editor state ---------- */
  const [activeClientId, setActiveClientId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [savingClient, setSavingClient] = useState(false);
  // tabs: client | shipping | warehousefees | receiptfees | orderfees | eorderfees | customfees
  const [tab, setTab] = useState('client');

  // Ensure non-admins cannot land on the Shipping tab even via state hacks
  useEffect(() => {
    if (!isAdmin && tab === 'shipping') setTab('client');
  }, [isAdmin, tab]);

  /* ---------- attachments (like Receipts) ---------- */
  const [clientAttachments, setClientAttachments] = useState([]);
  const [clientAttachmentsLoading, setClientAttachmentsLoading] = useState(false);
  const [clientAttachDesc, setClientAttachDesc] = useState('');
  const [clientPendingFiles, setClientPendingFiles] = useState([]);
  const [clientIsDragging, setClientIsDragging] = useState(false);
  const clientFileInputRef = useRef(null);
  // Bottom pane split + mode (attachments)
  const [bottomPaneMode, setBottomPaneMode] = useState(null); // 'attachments' | null
  const [attachmentsClientId, setAttachmentsClientId] = useState(null);
  const [attachmentsClientName, setAttachmentsClientName] = useState('');

  /* ---------- client-level fees ---------- */
  const [warehouseFees, setWarehouseFees] = useState([]);
  const [receiptFees, setReceiptFees] = useState([]);
  const [customFees, setCustomFees] = useState([]);
  const [orderFees, setOrderFees] = useState([]);
  const [eorderClientFees, setEorderClientFees] = useState([]); // NEW: clienteorderfees rows
  const [loadingFees, setLoadingFees] = useState(false);
  const [savingFee, setSavingFee] = useState(false);

  /* ---------- templates ---------- */
  const [tplWarehouse, setTplWarehouse] = useState([]);
  const [tplReceipt, setTplReceipt] = useState([]);
  const [tplCustom, setTplCustom] = useState([]);
  const [tplOrder, setTplOrder] = useState([]);
  const [tplEOrder, setTplEOrder] = useState([]); // NEW: template_eorderfees
  const [receiptTemplatePicker, setReceiptTemplatePicker] = useState({ open: false, templateId: '' });
  const [orderTemplatePicker, setOrderTemplatePicker] = useState({ open: false, templateId: '' });
  const [customTemplatePicker, setCustomTemplatePicker] = useState({ open: false, templateId: '' });

  // All storage units (whstorageunit), including picking types
  const [storageUnitOptions, setStorageUnitOptions] = useState([]);

  /* ---------- warehouses ---------- */
  const [warehouses, setWarehouses] = useState([]);

  // Shipping profiles per client
  const [shippingProviders, setShippingProviders] = useState([]);
  const [shippingProfiles, setShippingProfiles] = useState([]);
  const [shipNewIntegrationId, setShipNewIntegrationId] = useState('');

  // Money input strings for display formatting (2 decimals, comma separators, max 99,999.99)
  const [shipBalanceStr, setShipBalanceStr] = useState('');
  const [fundLimitStr, setFundLimitStr] = useState('');
  const [fundMinStr, setFundMinStr] = useState('');
  // Custom Order Fee name modal
  const [customOrderFee, setCustomOrderFee] = useState({ open: false, index: -1, value: '' });
  const closeCustomOrderFee = () => setCustomOrderFee({ open: false, index: -1, value: '' });
  const saveCustomOrderFee = async () => {
    const idx = customOrderFee.index;
    if (idx == null || idx < 0) return closeCustomOrderFee();
    const name = (customOrderFee.value || '').trim();
    if (!name) { closeCustomOrderFee(); return; }
    // force feeid=0 and set feename
    setOrderFees(prev => {
      const next = [...prev];
      const row = { ...next[idx], feeid: 0, feename: name };
      next[idx] = row;
      // save immediately
      crudFee({ fee: row, setFees: setOrderFees, singlePath: 'fees/order', collectionPath: 'fees/order' });
      return next;
    });
    closeCustomOrderFee();
  };

  // Custom E-Order Fee name modal
  const [customEOrderFee, setCustomEOrderFee] = useState({ open: false, index: -1, value: '' });
  const closeCustomEOrderFee = () => setCustomEOrderFee({ open: false, index: -1, value: '' });
  const saveCustomEOrderFee = async () => {
    const idx = customEOrderFee.index;
    if (idx == null || idx < 0) return closeCustomEOrderFee();
    const name = (customEOrderFee.value || '').trim();
    if (!name) { closeCustomEOrderFee(); return; }
    setEorderClientFees(prev => {
      const next = [...prev];
      const row = { ...next[idx], feename: name };
      next[idx] = row;
      crudFee({ fee: row, setFees: setEorderClientFees, singlePath: 'fees/eorder', collectionPath: 'fees/eorder' });
      return next;
    });
    closeCustomEOrderFee();
  };
  // Help modal for fee explanations
  const [help, setHelp] = useState({ open: false, title: '', body: '' });
  const openHelp = (key) => {
    if (key === 'auto') {
      setHelp({
        open: true,
        title: 'Auto Charge',
        body: 'Auto Charge ensures this charge will automatically be added to all new Warehouse Receipts.'
      });
    } else if (key === 'xpallets') {
      setHelp({
        open: true,
        title: 'xPallets',
        body: 'xPallets will take the charge specified and multiply it by the number of pallets on the receipt.'
      });
    } else if (key === 'usevol') {
      setHelp({
        open: true,
        title: 'Use Volume',
        body: 'Use Vol will use volume as the basis for charging.'
      });
    }
  };
  const closeHelp = () => setHelp({ open: false, title: '', body: '' });
  // Confirm + Notice modals (match other views)
  const [confirmState, setConfirmState] = useState({
    open: false,
    title: 'Confirm',
    message: '',
    confirmText: 'Yes',
    cancelText: 'Cancel',
    onConfirm: null,
  });
  const openConfirm = (message, onConfirm, opts = {}) => {
    const { title = 'Confirm', confirmText = 'Yes', cancelText = 'Cancel' } = opts || {};
    setConfirmState({ open: true, title, message, confirmText, cancelText, onConfirm });
  };
  const closeConfirm = () => setConfirmState({
    open: false, title: 'Confirm', message: '', confirmText: 'Yes', cancelText: 'Cancel', onConfirm: null
  });

  const [notify, setNotify] = useState({ open: false, type: 'success', title: '', message: '' });
  const openNotify = (type, title, message) => setNotify({ open: true, type, title, message });
  const closeNotify = () => setNotify(n => ({ ...n, open: false }));
  // Debounced auto-save timers per-fee row
  const feeSaveTimers = useRef({}); // key -> timeout id
  const clearAllFeeSaveTimers = () => {
    try {
      const timers = feeSaveTimers.current || {};
      Object.values(timers).forEach((t) => { try { clearTimeout(t); } catch {} });
    } catch {}
    feeSaveTimers.current = {};
  };
  const scheduleFeeAutoSave = ({ key, fee, singlePath, collectionPath }) => {
    const K = String(key);
    const cidAtSchedule = selected?.id; // capture current client id to avoid cross-client PUTs
    try { if (feeSaveTimers.current[K]) clearTimeout(feeSaveTimers.current[K]); } catch {}
    feeSaveTimers.current[K] = setTimeout(() => {
      // If user switched clients/tabs, drop this scheduled save to prevent 404 on wrong client
      if (selected?.id !== cidAtSchedule) { try { delete feeSaveTimers.current[K]; } catch {}; return; }
      crudFee({ fee, setFees: undefined, singlePath, collectionPath });
      try { delete feeSaveTimers.current[K]; } catch {}
    }, 500);
  };

  // Clear any pending auto-saves when switching clients or tabs, or on unmount
  useEffect(() => { clearAllFeeSaveTimers(); }, [selected?.id, tab]);
  useEffect(() => () => { clearAllFeeSaveTimers(); }, []);

  // ---- Money helpers (format/parse/clamp) ----
  const MAX_MONEY = 99999.99; // no more than tens of thousands
  const clampMoney = (n) => {
    const x = Number(n);
    if (!Number.isFinite(x)) return 0;
    return Math.max(0, Math.min(MAX_MONEY, Math.round(x * 100) / 100));
  };
  const formatMoney = (n) => clampMoney(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const parseMoney = (s) => {
    if (typeof s !== 'string') return 0;
    const cleaned = s.replace(/[^0-9.]/g, '');
    const v = parseFloat(cleaned);
    return clampMoney(Number.isFinite(v) ? v : 0);
  };
  const receiptTemplateValue = (tpl) => {
    if (!tpl) return '';
    return String(tpl.id ?? tpl.name ?? '');
  };
  const selectedReceiptTemplate = useMemo(() => {
    const val = String(receiptTemplatePicker.templateId || '');
    return tplReceipt.find((tpl) => receiptTemplateValue(tpl) === val);
  }, [receiptTemplatePicker.templateId, tplReceipt]);
  const orderTemplateValue = (tpl) => {
    if (!tpl) return '';
    return String(tpl.id ?? tpl.feename ?? '');
  };
  const selectedOrderTemplate = useMemo(() => {
    const val = String(orderTemplatePicker.templateId || '');
    return tplOrder.find((tpl) => orderTemplateValue(tpl) === val);
  }, [orderTemplatePicker.templateId, tplOrder]);
  const customTemplateValue = (tpl) => {
    if (!tpl) return '';
    return String(tpl.id ?? tpl.name ?? '');
  };
  const selectedCustomTemplate = useMemo(() => {
    const val = String(customTemplatePicker.templateId || '');
    return tplCustom.find((tpl) => customTemplateValue(tpl) === val);
  }, [customTemplatePicker.templateId, tplCustom]);

  // --- Shipping providers + client-assigned profiles ---
  const fetchShippingProviders = async () => {
    try {
      const res = await fetch(`${API}/api/shippingintegrations/providers?enabled=1`);
      const j = await res.json();
      setShippingProviders(Array.isArray(j?.data) ? j.data : []);
    } catch {
      setShippingProviders([]);
    }
  };
  const fetchClientShippingProfiles = async (cid) => {
    if (!cid) { setShippingProfiles([]); return; }
    try {
      const res = await fetch(`${API}/api/shippingintegrations/client-profiles/${cid}`);
      const j = await res.json();
      const rows = Array.isArray(j?.data) ? j.data : [];
      // Normalize backend decimal to whole-number percent (1-100) for UI
      const normalized = rows.map(sp => {
        const dec = Number(sp['3plmarkup'] || 0);
        let pct = Math.round(dec * 100);
        if (!Number.isFinite(pct)) pct = 1;
        pct = Math.max(1, Math.min(100, pct));
        return { ...sp, markupPct: pct };
      });
      setShippingProfiles(normalized);
    } catch {
      setShippingProfiles([]);
    }
  };
  useEffect(() => { if (isAdmin) fetchShippingProviders(); }, [isAdmin]);
  useEffect(() => { if (isAdmin && selected?.id) fetchClientShippingProfiles(selected.id); }, [selected?.id, isAdmin]);

  // Initialize formatted strings when switching clients
  useEffect(() => {
    setShipBalanceStr(formatMoney(selected?.shippingbalance ?? 0));
    setFundLimitStr(formatMoney(selected?.shippingfundlimit ?? 0));
    setFundMinStr(formatMoney(selected?.shippingfundminthreshold ?? 0));
  }, [selected?.id]);

  /* ---------- pagination helpers ---------- */
  const page = useMemo(
    () => Math.floor((meta.offset || 0) / (limit || DEFAULT_LIMIT)) + 1,
    [meta.offset, limit]
  );
  const lastPage = useMemo(
    () => Math.max(1, Math.ceil((meta.total || 0) / (limit || DEFAULT_LIMIT))),
    [meta.total, limit]
  );

  // --- Split sizes (persisted) ---
  const getInitialSplitSizes = () => {
    try {
      const stored = localStorage.getItem('clientSplitSizes');
      if (stored) {
        const s = JSON.parse(stored);
        if (Array.isArray(s) && s.length === 2) return s;
      }
      const rightPct = parseInt(localStorage.getItem('clientsSplitPct') || '', 10);
      if (!Number.isNaN(rightPct)) return [100 - rightPct, rightPct];
    } catch {}
    return [60, 40];
  };
  const [splitSizes, setSplitSizes] = useState(getInitialSplitSizes);

  /* -----------------------------------------------------------
     Fetch utilities
     ----------------------------------------------------------- */
  const fetchJSON = async (url, opts) => {
    const res = await fetch(url, opts);
    if (!res.ok) {
      let msg = `${res.status} ${res.statusText}`;
      try {
        const b = await res.json();
        if (b?.error) msg = b.error;
      } catch {}
      throw new Error(msg);
    }
    return res.json();
  };

  const fetchClients = async (offset = 0, lim = limit) => {
    const key = JSON.stringify({
      offset,
      limit: lim,
      q: (q || ''),
      activeOnly: !showInactive,
      sortField,
      sortOrder,
    });
    if (lastFetchKeyRef.current === key) return;
    lastFetchKeyRef.current = key;
    setLoadingList(true);
    try {
      const params = new URLSearchParams({ limit: lim, offset });
      if (q) params.set('q', q);
      if (!showInactive) params.set('activeOnly', '1');
      params.set('sortField', sortField);
      params.set('sortOrder', sortOrder);
      const json = await fetchJSON(`${API}/api/clients?${params.toString()}`);
      setClients(json.data || []);
      setMeta(json.meta || { total: 0, limit: lim, offset });
    } catch (e) {
      console.error('GET /api/clients', e);
      setClients([]);
      setMeta({ total: 0, limit: lim, offset });
    } finally {
      setLoadingList(false);
    }
  };

  const fetchClientDetail = async (id) => {
    try {
      const json = await fetchJSON(`${API}/api/clients/${id}`);
      setSelected({ ...emptyClient, ...json });
    } catch (e) {
      console.error(e);
      setSelected(null);
    }
  };

  // Add a shipping profile association for this client
  const addShippingProfile = async () => {
    if (!isAdmin) { openNotify('warn', 'Not Allowed', 'Only admins can modify shipping profiles.'); return; }
    if (!selected?.id) { openNotify('warn', 'No Client', 'Select a client first.'); return; }
    const integrationId = parseInt(shipNewIntegrationId, 10);
    if (!integrationId) { openNotify('warn', 'Missing Provider', 'Choose a shipping provider to add.'); return; }
    try {
      const res = await fetch(`${API}/api/shippingintegrations/client-profiles/${selected.id}/${integrationId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        // Do not send vendormarkup/juicymarkup/3plmarkup; backend copies defaults where needed
        body: JSON.stringify({})
      });
      const j = await res.json();
      if (!res.ok || j?.error) throw new Error(j?.error || 'Failed to add profile');
      setShipNewIntegrationId('');
      await fetchClientShippingProfiles(selected.id);
    } catch (e) {
      openNotify('error', 'Add Profile Failed', e.message);
    }
  };

  const saveShippingProfile = async (row) => {
    if (!isAdmin) { openNotify('warn', 'Not Allowed', 'Only admins can modify shipping profiles.'); return; }
    if (!selected?.id || !row?.shipping_integration_id) return;
    try {
      const res = await fetch(`${API}/api/shippingintegrations/client-profiles/${selected.id}/${row.shipping_integration_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          // Convert whole-number percent back to decimal (e.g., 15 -> 0.15)
          '3plmarkup': (() => {
            let n = parseInt(row.markupPct, 10);
            if (!Number.isFinite(n)) n = 1;
            n = Math.max(1, Math.min(100, n));
            return n / 100;
          })(),
        })
      });
      const j = await res.json();
      if (!res.ok || j?.error) throw new Error(j?.error || 'Failed to save');
      await fetchClientShippingProfiles(selected.id);
      openNotify('success', 'Saved', 'Shipping profile updated.');
    } catch (e) {
      openNotify('error', 'Save Failed', e.message);
    }
  };

  const deleteShippingProfile = async (row) => {
    if (!isAdmin) { openNotify('warn', 'Not Allowed', 'Only admins can modify shipping profiles.'); return; }
    if (!selected?.id || !row?.shipping_integration_id) return;
    openConfirm('Remove this shipping profile?', async () => {
      try {
        const res = await fetch(`${API}/api/shippingintegrations/client-profiles/${selected.id}/${row.shipping_integration_id}`, { method: 'DELETE' });
        const j = await res.json().catch(() => ({}));
        if (!res.ok || j?.error) throw new Error(j?.error || 'Delete failed');
        await fetchClientShippingProfiles(selected.id);
      } catch (e) {
        openNotify('error', 'Delete Failed', e.message);
      }
    }, { title: 'Confirm Delete', confirmText: 'Delete', cancelText: 'Cancel' });
  };

  const fetchClientFees = async (cid) => {
    setLoadingFees(true);
    try {
      const wf = await fetchJSON(`${API}/api/clients/${cid}/fees/warehouse`).catch(() => []);
      const rf = await fetchJSON(`${API}/api/clients/${cid}/fees/receipt`).catch(() => []);
      const cf = await fetchJSON(`${API}/api/clients/${cid}/fees/custom`).catch(() => []);
      const ofe = await fetchJSON(`${API}/api/clients/${cid}/fees/order`).catch(() => []);
      const eocf = await fetchJSON(`${API}/api/clients/${cid}/fees/eorder`).catch(() => []); // NEW
      setWarehouseFees(Array.isArray(wf) ? wf : []);
      setReceiptFees(Array.isArray(rf) ? rf : []);
      setCustomFees(Array.isArray(cf) ? cf : []);
      setOrderFees(Array.isArray(ofe) ? ofe : []);
      setEorderClientFees(Array.isArray(eocf) ? eocf : []);
    } catch (e) {
      // Do not propagate refresh errors; leave prior state if something transient fails.
      console.warn('fetchClientFees: partial failure', e?.message || e);
    } finally {
      setLoadingFees(false);
    }
  };

  const fetchTemplates = async () => {
    // Fetch eOrder fee templates first — decouple from other templates
    let eo = [];
    try { eo = await fetchJSON(`${API}/api/templates/eorderfees`); }
    catch (e) { eo = []; }
    if (!Array.isArray(eo) || eo.length === 0) {
      // Fallback: try order fee templates as source for names
      try {
        const o = await fetchJSON(`${API}/api/templates/orderfees`);
        if (Array.isArray(o) && o.length) {
          eo = o.map(t => ({ id: t.id, feename: t.feename, description: t.description, amt: t.amt, auto: t.auto }));
        }
      } catch {}
    }
    setTplEOrder(Array.isArray(eo) ? eo : []);

    // Fetch the other templates independently; failures won't clear eOrder list
    try { const w = await fetchJSON(`${API}/api/templates/warehousefees`); setTplWarehouse(Array.isArray(w) ? w : []); } catch { setTplWarehouse([]); }
    try { const r = await fetchJSON(`${API}/api/templates/receiptfees`);   setTplReceipt(Array.isArray(r) ? r : []); } catch { setTplReceipt([]); }
    try { const c = await fetchJSON(`${API}/api/templates/customfees`);    setTplCustom(Array.isArray(c) ? c : []); } catch { setTplCustom([]); }
    try { const o = await fetchJSON(`${API}/api/templates/orderfees`);     setTplOrder(Array.isArray(o) ? o : []); } catch { setTplOrder([]); }
  };

  const fetchWarehouses = async () => {
    try {
      const json = await fetchJSON(`${API}/api/warehouses`);
      setWarehouses(Array.isArray(json) ? json : []);
    } catch (e) {
      console.error('GET /api/warehouses', e);
      setWarehouses([]);
    }
  };

  // Load all storage units (whstorageunit) — include picking types
  const fetchStorageUnits = async () => {
    try {
      const res = await fetch(`${API}/api/packagingunits/storageunits`);
      const j = await res.json();
      const list = Array.isArray(j) ? j : [];
      setStorageUnitOptions(list.map(su => ({ id: su.id, name: su.name })));
    } catch (e) {
      setStorageUnitOptions([]);
    }
  };

  /* -----------------------------------------------------------
     Initial loads
     ----------------------------------------------------------- */
  useEffect(() => {
    fetchClients(meta.offset, limit);
  }, [sortField, sortOrder, showInactive, limit]);

  // Live-search: fetch whenever `q` changes (debounced)
  useEffect(() => {
    const handle = setTimeout(() => {
      // Always reset to first page when searching
      fetchClients(0, limit);
    }, 300); // debounce 300ms
    
    return () => clearTimeout(handle);
  }, [q, limit]); // refetch if page size changes while typing

  // persist search query
  useEffect(() => {
    try { localStorage.setItem('clientsQ', q || ''); } catch {}
  }, [q]);

  useEffect(() => {
    fetchTemplates();
    fetchWarehouses();
    fetchStorageUnits();
  }, []);

  // Ensure fee templates are available when a user (including non-admin internal users) opens the eOrder Fees tab
  useEffect(() => {
    if (tab === 'eorderfees' && (!Array.isArray(tplEOrder) || tplEOrder.length === 0)) {
      fetchTemplates();
    }
  }, [tab]);

  useEffect(() => {
    if (selected?.id) fetchClientFees(selected.id);
  }, [selected?.id]);

  // Load client attachments for bottom pane
  useEffect(() => {
    if (!attachmentsClientId || bottomPaneMode !== 'attachments') return;
    fetchClientAttachments(attachmentsClientId);
  }, [attachmentsClientId, bottomPaneMode]);

  // --- Topbar title (set after render; clear on unmount) ---
  useEffect(() => {
    const subtitle = meta?.total > 0 ? `(${Number(meta.total).toLocaleString()} found)` : '';
    window.dispatchEvent(new CustomEvent('topbar:setTitle', {
      detail: { title: 'Clients ', subtitle }
    }));
    return () => {
      window.dispatchEvent(new CustomEvent('topbar:clearTitle'));
    };
  }, [meta?.total]);

  /* -----------------------------------------------------------
     Pagination navigation
     ----------------------------------------------------------- */
  const gotoPage = (p) => {
    const nextOffset = Math.max(0, (p - 1) * limit);
    fetchClients(nextOffset, limit);
  };

  const handleLimitUpdate = () => {
    let v = Number(limitInput);
    if (isNaN(v)) v = limit;
    if (v < 1) v = 1;
    if (v > 500) v = 500;
    setLimit(v);
    setLimitInput(String(v));
    window.localStorage.setItem('clientsLimit', v);
    fetchClients(0, v);
  };

  // sorting helper
  const handleSort = (field, e) => {
    if (e?.target && typeof e.target.closest === 'function' && e.target.closest('.col-resizer')) return;
    if (sortField === field) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortField(field);
      setSortOrder('ASC');
    }
  };

  /* -----------------------------------------------------------
     Client CRUD
     ----------------------------------------------------------- */
  const onSelectClient = async (id) => {
    await fetchClientDetail(id);
    // Ensure the clicked client row is visually selected/highlighted
    try { setActiveClientId(id); } catch {}
    setTab('client');
  };

  const onNewClient = () => {
    setSelected({ ...emptyClient });
    setWarehouseFees([]);
    setReceiptFees([]);
    setOrderFees([]);
    setEorderClientFees([]);
    setTab('client');
  };

  // Reset saved column widths for the Clients table
  const resetClientColumns = () => {
    try { localStorage.removeItem(LS_CLIENT_COLWIDTHS); } catch {}
    // Enter auto-fit so the browser lays out columns from content
    setClientAutoFit(true);
    const afterPaint = () => {
      const headerRow = clientHeaderRef.current;
      const hasRows = !!clientTableRef.current?.tBodies?.[0]?.rows?.length;
      if (!headerRow || !hasRows) { requestAnimationFrame(afterPaint); return; }
      const next = {};
      for (let i = 0; i < CLIENT_COLS.length; i++) {
        next[CLIENT_COLS[i]] = measureClientColWidth(i);
      }
      setClientColWidths(next);
      try { localStorage.setItem(LS_CLIENT_COLWIDTHS, JSON.stringify(next)); } catch {}
      // allow a tick for widths to apply, then exit auto-fit
      setTimeout(() => setClientAutoFit(false), 0);
    };
    requestAnimationFrame(afterPaint);
  };

  const deleteClient = async (id) => {
    openConfirm('Delete this client?', async () => {
      try {
        await fetchJSON(`${API}/api/clients/${id}`, { method: 'DELETE' });
        await fetchClients(meta.offset, limit);
        if (selected?.id === id) setSelected(null);
        openNotify('success', 'Deleted', 'Client removed.');
      } catch (e) {
        openNotify('error', 'Delete Failed', e.message);
      }
    }, { title: 'Confirm Delete', confirmText: 'Delete', cancelText: 'Cancel' });
  };

  // ---- Client Attachments CRUD ----
  const removeClientPendingFile = (index) => setClientPendingFiles(prev => prev.filter((_, i) => i !== index));
  const fetchClientAttachments = async (clientId) => {
    if (!clientId) { setClientAttachments([]); return; }
    setClientAttachmentsLoading(true);
    try {
      const data = await fetchJSON(`${API}/api/clients/${clientId}/attachments`);
      setClientAttachments(Array.isArray(data) ? data : []);
    } catch {
      setClientAttachments([]);
    } finally {
      setClientAttachmentsLoading(false);
    }
  };
  const uploadClientFiles = async () => {
    if (!attachmentsClientId || !clientPendingFiles?.length) return;
    const fd = new FormData();
    clientPendingFiles.forEach(f => fd.append('files', f));
    if (clientAttachDesc) fd.append('description', clientAttachDesc);
    try {
      const res = await fetch(`${API}/api/clients/${attachmentsClientId}/attachments`, { method: 'POST', body: fd });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || j?.error) throw new Error(j?.error || `Upload failed (${res.status})`);
      setClientAttachDesc('');
      setClientPendingFiles([]);
      await fetchClientAttachments(attachmentsClientId);
      // refresh counts in the list
      fetchClients(meta.offset, limit);
      openNotify('success', 'Uploaded', 'Attachment(s) uploaded.');
    } catch (e) {
      openNotify('error', 'Upload Failed', e.message || 'Upload failed');
    }
  };
  const deleteClientAttachment = async (attId) => {
    if (!attachmentsClientId || !attId) return;
    openConfirm('Delete this attachment?', async () => {
      try {
        await fetchJSON(`${API}/api/clients/${attachmentsClientId}/attachments/${attId}`, { method: 'DELETE' });
        await fetchClientAttachments(attachmentsClientId);
        // refresh counts in the list
        fetchClients(meta.offset, limit);
      } catch (e) {
        openNotify('error', 'Delete Failed', e.message);
      }
    }, { title: 'Confirm Delete', confirmText: 'Delete', cancelText: 'Cancel' });
  };
  const onClientDrop = (e) => { e.preventDefault(); setClientIsDragging(false); const f = e.dataTransfer?.files; if (f && f.length) setClientPendingFiles(Array.from(f)); };
  const onClientDragOver = (e) => { e.preventDefault(); setClientIsDragging(true); };
  const onClientDragLeave = () => setClientIsDragging(false);

  const onShowClientAttachments = (clientId, clientName) => {
    setAttachmentsClientId(clientId);
    setAttachmentsClientName(clientName || '');
    setBottomPaneMode('attachments');
  };

  const saveClient = async () => {
    if (!selected) return;
    setSavingClient(true);
    try {
      const method = selected.id ? 'PUT' : 'POST';
      const url = selected.id ? `${API}/api/clients/${selected.id}` : `${API}/api/clients`;
      const body = { ...selected };
      await fetchJSON(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      await fetchClients(meta.offset, limit);
      if (method === 'POST') {
        await fetchClientDetail(body.id ?? body.insertId ?? body.id);
      }
      openNotify('success', 'Saved', 'Client saved.');
    } catch (e) {
      openNotify('error', 'Save Failed', e.message);
    } finally {
      setSavingClient(false);
    }
  };

  /* -----------------------------------------------------------
     Shared fee helpers
     ----------------------------------------------------------- */
  const updateLocalFee = (setter, idx, patch) =>
    setter((f) => {
      const next = f.slice();
      next[idx] = { ...next[idx], ...patch };
      return next;
    });

  /* ---------- Warehouse fees (clientwarehousefees) ---------- */
  const addWarehouseFee = async () => {
    if (!selected?.id) { openNotify('warn', 'No Client', 'Select a client first.'); return; }
    const firstSU = storageUnitOptions[0]?.id || (tplWarehouse[0]?.whstorageunit || 0);
    const defaultTemplate = tplWarehouse.find(t => t.whstorageunit === firstSU) || { fee: 0 };
    const newFee = {
      id: null,
      deleted: 0,
      clientid: selected.id,
      warehouseid: warehouses[0]?.id ?? 1,
      whstorageunit: firstSU,
      fee: defaultTemplate.fee,
      _new: true,
    };
    setWarehouseFees((f) => [...f, newFee]);
    await crudFee({ fee: newFee, setFees: setWarehouseFees, singlePath: 'fees/warehouse', collectionPath: 'fees/warehouse' });
  };

  /* ---------- Receipt fees (clientreceiptfees) ---------- */
  const addReceiptFee = () => {
    if (!selected?.id) { openNotify('warn', 'No Client', 'Select a client first.'); return; }
    if (!Array.isArray(tplReceipt) || tplReceipt.length === 0) {
      openNotify('warn', 'No Receiving Templates', 'No receiving fee templates are available. Create templates first.');
      return;
    }
    const initial = receiptTemplateValue(tplReceipt[0]);
    setReceiptTemplatePicker({ open: true, templateId: initial });
  };

  const closeReceiptTemplatePicker = () => setReceiptTemplatePicker({ open: false, templateId: '' });

  const applyReceiptTemplateValues = (fee, tpl) => {
    if (!tpl) return fee;
    const toNum = (val, fallback = 0) => {
      const n = Number(val);
      return Number.isFinite(n) ? n : fallback;
    };
    return {
      ...fee,
      name: tpl.name || fee.name,
      description: tpl.description || '',
      amt: toNum(tpl.amt, fee.amt ?? 0),
      auto: toNum(tpl.auto, fee.auto ?? 0),
      xpallets: toNum(tpl.xpallets, fee.xpallets ?? 0),
      usevolume: toNum(tpl.usevolume, fee.usevolume ?? 0),
      _amtStr: undefined,
    };
  };

  const confirmAddReceiptFee = async () => {
    if (!selected?.id) return;
    const tpl = tplReceipt.find((t) => receiptTemplateValue(t) === String(receiptTemplatePicker.templateId || ''));
    if (!tpl) {
      openNotify('warn', 'Select Template', 'Choose a receiving fee template before adding.');
      return;
    }
    const defaultWarehouse = Number(warehouses?.[0]?.id) || 1;
    const newFee = applyReceiptTemplateValues({
      id: null,
      deleted: 0,
      clientid: selected.id,
      warehouseid: defaultWarehouse,
      type: 0,
      name: '',
      description: '',
      amt: 0,
      auto: 0,
      xpallets: 0,
      usevolume: 0,
      _new: true,
    }, tpl);
    setReceiptFees((f) => [...f, newFee]);
    await crudFee({ fee: newFee, setFees: setReceiptFees, singlePath: 'fees/receipt', collectionPath: 'fees/receipt' });
    closeReceiptTemplatePicker();
  };

  const applyOrderTemplateValues = (fee, tpl) => {
    if (!tpl) return fee;
    return {
      ...fee,
      feeid: Number(tpl.id) || 0,
      feename: tpl.feename || fee.feename || '',
      description: tpl.description || '',
      amt: Number.isFinite(Number(tpl.amt)) ? Number(tpl.amt) : (fee.amt ?? 0),
      auto: Number.isFinite(Number(tpl.auto)) ? Number(tpl.auto) : (fee.auto ?? 0),
      _amtStr: undefined,
    };
  };

  const applyCustomTemplateValues = (fee, tpl) => {
    if (!tpl) return fee;
    const toNum = (val, fallback = 0) => {
      const n = Number(val);
      return Number.isFinite(n) ? n : fallback;
    };
    return {
      ...fee,
      name: tpl.name || fee.name,
      description: tpl.description || '',
      amt: toNum(tpl.amt, fee.amt ?? 0),
      auto: toNum(tpl.auto, fee.auto ?? 0),
      xpallets: toNum(tpl.xpallets, fee.xpallets ?? 0),
      usevolume: toNum(tpl.usevolume, fee.usevolume ?? 0),
      _amtStr: undefined,
    };
  };

  /* ---------- Custom fees (clientcustomfees) ---------- */
  const addCustomFee = () => {
    if (!selected?.id) { openNotify('warn', 'No Client', 'Select a client first.'); return; }
    if (!Array.isArray(tplCustom) || tplCustom.length === 0) {
      openNotify('warn', 'No Custom Fee Templates', 'No custom fee templates are available. Create templates first.');
      return;
    }
    const initial = customTemplateValue(tplCustom[0]);
    setCustomTemplatePicker({ open: true, templateId: initial });
  };

  const closeCustomTemplatePicker = () => setCustomTemplatePicker({ open: false, templateId: '' });

  const confirmAddCustomFee = async () => {
    if (!selected?.id) return;
    const tpl = tplCustom.find((t) => customTemplateValue(t) === String(customTemplatePicker.templateId || ''));
    if (!tpl) {
      openNotify('warn', 'Select Template', 'Choose a custom fee template before adding.');
      return;
    }
    const newFee = applyCustomTemplateValues({
      id: null,
      deleted: 0,
      clientid: selected.id,
      warehouseid: warehouses[0]?.id ?? 1,
      type: 0,
      name: '',
      description: '',
      amt: 0,
      auto: 0,
      xpallets: 0,
      usevolume: 0,
      _new: true,
    }, tpl);
    setCustomFees((f) => [...f, newFee]);
    await crudFee({ fee: newFee, setFees: setCustomFees, singlePath: 'fees/custom', collectionPath: 'fees/custom' });
    closeCustomTemplatePicker();
  };

  /* ---------- Order fees (clientorderfees) ---------- */
  const addOrderFee = () => {
    if (!selected?.id) { openNotify('warn', 'No Client', 'Select a client first.'); return; }
    if (!Array.isArray(tplOrder) || tplOrder.length === 0) {
      openNotify('warn', 'No Order Templates', 'No order fee templates are available. Create templates first.');
      return;
    }
    const initial = orderTemplateValue(tplOrder[0]);
    setOrderTemplatePicker({ open: true, templateId: initial });
  };

  const closeOrderTemplatePicker = () => setOrderTemplatePicker({ open: false, templateId: '' });

  const confirmAddOrderFee = async () => {
    if (!selected?.id) return;
    const tpl = tplOrder.find((t) => orderTemplateValue(t) === String(orderTemplatePicker.templateId || ''));
    if (!tpl) {
      openNotify('warn', 'Select Template', 'Choose an order fee template before adding.');
      return;
    }
    const firstWhId = Number(warehouses?.[0]?.id);
    if (!Number.isFinite(firstWhId) || firstWhId <= 0) {
      openNotify('warn', 'Warehouses Not Loaded', 'Please wait for warehouses to load, then try again.');
      try { await fetchWarehouses(); } catch {}
      return;
    }
    const newFee = applyOrderTemplateValues({
      id: null,
      deleted: 0,
      clientid: selected.id,
      warehouseid: firstWhId,
      feeid: 0,
      type: 0,
      feename: '',
      description: '',
      amt: 0,
      auto: 0,
      _new: true,
    }, tpl);
    setOrderFees((f) => [...f, newFee]);
    await crudFee({ fee: newFee, setFees: setOrderFees, singlePath: 'fees/order', collectionPath: 'fees/order' });
    closeOrderTemplatePicker();
  };

  const crudFee = async ({ fee, setFees, singlePath, collectionPath }) => {
    if (!selected?.id) return;
    setSavingFee(true);
    try {
      const method = fee.id ? 'PUT' : 'POST';
      const url = fee.id
        ? `${API}/api/clients/${selected.id}/${singlePath}/${fee.id}`
        : `${API}/api/clients/${selected.id}/${collectionPath}`;
console.log( url );
        
      const payload = { ...fee };
      delete payload._new;
      delete payload._amtStr;
      delete payload._feeStr;
      await fetchJSON(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      await fetchClientFees(selected.id);
    } catch (e) {
      openNotify('error', 'Save Failed', e.message);
    } finally {
      setSavingFee(false);
    }
  };

  const deleteFee = async ({ fee, singlePath }) => {
    if (!selected?.id) return;
    if (!fee.id) {
      // remove the draft row only
      setEorderClientFees((f) => f.filter((x) => x !== fee));
      setWarehouseFees((f) => f.filter((x) => x !== fee));
      setReceiptFees((f) => f.filter((x) => x !== fee));
      setCustomFees((f) => f.filter((x) => x !== fee));
      setOrderFees((f) => f.filter((x) => x !== fee));
      return;
    }
    openConfirm('Delete this fee?', async () => {
      setSavingFee(true);
      try {
        await fetchJSON(`${API}/api/clients/${selected.id}/${singlePath}/${fee.id}`, { method: 'DELETE' });
        await fetchClientFees(selected.id);
        openNotify('success', 'Deleted', 'Fee removed.');
      } catch (e) {
        openNotify('error', 'Delete Failed', e.message);
      } finally {
        setSavingFee(false);
      }
    }, { title: 'Confirm Delete', confirmText: 'Delete', cancelText: 'Cancel' });
  };

  /* ---------- eOrder fees (clienteorderfees) ---------- */
  const addEOrderClientFee = async () => {
    if (!selected?.id) { openNotify('warn', 'No Client', 'Select a client first.'); return; }
    // Preselect first template; user can change inline
    const t = tplEOrder[0] || { feename: '', description: '', amt: 0, auto: 0 };
    const newFee = {
      id: null,
      deleted: 0,
      clientid: selected.id,
      warehouseid: warehouses[0]?.id ?? 1,
      feename: t.feename,
      description: t.description,
      amt: Number(t.amt ?? 0),
      auto: Number(t.auto || 0),
      fixedqty: 0,
      _new: true,
    };
    setEorderClientFees((f) => [...f, newFee]);
    await crudFee({ fee: newFee, setFees: setEorderClientFees, singlePath: 'fees/eorder', collectionPath: 'fees/eorder' });
  };

  /* -----------------------------------------------------------
     Render helpers
     ----------------------------------------------------------- */
  const renderList = () => (
    <>
      <div className="clients-toolbar">
        <div className="search">
          <FaSearch />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name / email"
          />
          {q && (
            <button
              className="clear"
              onClick={() => {
                setQ('');
                try { localStorage.setItem('clientsQ', ''); } catch {}
                fetchClients(0, limit);
              }}
            >
              <FaTimes />
            </button>
          )}
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <input
            type="checkbox"
            checked={showInactive}
            onChange={e => {
              setShowInactive(e.target.checked);
              setTimeout(() => fetchClients(0, limit), 0);
            }}
            style={{ marginRight: 4 }}
          />
          Show inactive clients
        </label>
        <button className="btn" onClick={() => fetchClients(0, limit)} title="Search">
          <FaSearch style={{ marginRight: 6 }} />
          Search
        </button>
        <button className="btn primary" onClick={onNewClient}>
          <FaPlus /> New
        </button>
        <button className="btn" onClick={resetClientColumns} title="Reset column widths">
          <FaColumns style={{ marginRight: 6 }} />
          Reset Columns
        </button>
      </div>

      {loadingList ? (
        <div className="loading">Loading…</div>
      ) : (
        <Split
          direction="vertical"
          sizes={bottomPaneMode ? [splitSizes[0], splitSizes[1]] : [100, 0]}
          minSize={bottomPaneMode ? [200, 200] : [200, 0]}
          gutterSize={bottomPaneMode ? 6 : 0}
          onDragEnd={(sizes) => { setSplitSizes(sizes); try { localStorage.setItem('clientSplitSizes', JSON.stringify(sizes)); } catch {} }}
          className="clients-split"
          style={{ height: 'calc(100vh - 160px)', minHeight: 0 }}
        >
          {/* Top List Pane */}
          <div className={`clients-table-wrapper ${clientAutoFit ? 'auto-fit' : ''}`}>
            {(() => {
              const haveSavedWidths = Object.keys(clientColWidths || {}).length === CLIENT_COLS.length;
              const tableClass = `clients-table${haveSavedWidths ? ' fixed' : ''}`;
              return (
                <table className={tableClass} ref={clientTableRef}>
                  <colgroup>
                    {CLIENT_COLS.map((k, idx) => {
                      const isLast = idx === CLIENT_COLS.length - 1;
                      const savedW = clientColWidths[k];
                      const autoW = autoClientColWidths[k];
                      const defW = DEFAULT_CLIENT_COLWIDTHS[k];
                      if (clientAutoFit) {
                        return <col key={k} className={isLast ? 'stretch' : undefined} />;
                      }
                      const wRaw = savedW || autoW || defW;
                      const min = k === 'actions' ? 36 : 40;
                      const max = k === 'actions' ? 160 : 800;
                      const w = Math.max(min, Math.min(max, Number(wRaw || 0)));
                      const style = (!isLast && w) ? { width: `${w}px` } : undefined;
                      const cls = isLast ? 'stretch' : undefined;
                      return <col key={k} className={cls} style={style} />;
                    })}
                  </colgroup>
                  <thead>
                    <tr ref={clientHeaderRef}>
                      <th style={{ position: 'relative' }}>
                        Actions
                        <span
                          className="col-resizer"
                          onMouseDown={(e)=>startClientResize('actions', e)}
                          onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }}
                          onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitClientCol('actions'); }}
                        />
                      </th>
                      <th onClick={(e) => handleSort('status', e)} className={sortField === 'status' ? 'active-sort' : ''} style={{ position: 'relative' }}>
                        Status {sortField === 'status' ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}
                        <span
                          className="col-resizer"
                          onMouseDown={(e)=>startClientResize('status', e)}
                          onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }}
                          onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitClientCol('status'); }}
                        />
                      </th>
                      <th onClick={(e) => handleSort('id', e)} className={sortField === 'id' ? 'active-sort' : ''} style={{ position: 'relative' }}>
                        ID {sortField === 'id' ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}
                        <span
                          className="col-resizer"
                          onMouseDown={(e)=>startClientResize('id', e)}
                          onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }}
                          onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitClientCol('id'); }}
                        />
                      </th>
                      <th onClick={(e) => handleSort('name', e)} className={sortField === 'name' ? 'active-sort' : ''} style={{ position: 'relative' }}>
                        Name {sortField === 'name' ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}
                        <span
                          className="col-resizer"
                          onMouseDown={(e)=>startClientResize('name', e)}
                          onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }}
                          onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitClientCol('name'); }}
                        />
                      </th>
                      <th onClick={(e) => handleSort('contact', e)} className={sortField === 'contact' ? 'active-sort' : ''} style={{ position: 'relative' }}>
                        Contact {sortField === 'contact' ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}
                        <span
                          className="col-resizer"
                          onMouseDown={(e)=>startClientResize('contact', e)}
                          onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }}
                          onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitClientCol('contact'); }}
                        />
                      </th>
                      <th onClick={(e) => handleSort('email', e)} className={sortField === 'email' ? 'active-sort' : ''} style={{ position: 'relative' }}>
                        Email {sortField === 'email' ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}
                        <span
                          className="col-resizer"
                          onMouseDown={(e)=>startClientResize('email', e)}
                          onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }}
                          onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitClientCol('email'); }}
                        />
                      </th>
                      <th onClick={(e) => handleSort('paymentterms', e)} className={sortField === 'paymentterms' ? 'active-sort' : ''} style={{ position: 'relative' }}>
                        Terms {sortField === 'paymentterms' ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}
                        {/* Last column stretches; omit resizer to keep layout intuitive */}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {clients.length ? (
                clients.map((c) => (
                  <tr
                    key={c.id}
                    className={activeClientId === c.id ? 'active' : ''}
                    onClick={() => setActiveClientId(c.id)}
                  >
                          <td
                            className="actions"
                            onClick={e => e.stopPropagation()}
                            style={{ whiteSpace: 'nowrap' }}
                          >
                            <div className='actions'>
                              <button
                                className="mini-btn"
                                title="Client Attachments"
                                onClick={() => onShowClientAttachments(c.id, c.name)}
                                style={{ color: Number(c.attachments_count || 0) > 0 ? '#21bb50' : undefined }}
                              >
                                <FaPaperclip size={16} />
                              </button>
                              <button
                                className="mini-btn"
                                title="Edit"
                                onClick={() => onSelectClient(c.id)}
                              >
                                <FaEdit size={16}/>
                              </button>
                              <button
                                className="mini-btn danger"
                                title="Delete"
                                onClick={() => deleteClient(c.id)}
                              >
                                <FaTrash size={16}/>
                              </button>
                            </div>
                          </td>
                          <td>{c.status ? 'Active' : 'Inactive'}</td>
                          <td>{c.id}</td>
                          <td>{c.name}</td>
                          <td>{c.contact}</td>
                          <td>{c.email}</td>
                          <td>{c.paymentterms}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={7}>No clients</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              );
            })()}

            <div className="pagination">
              <button className="btn" disabled={page <= 1} onClick={() => gotoPage(1)}>
                &laquo;
              </button>
              <button className="btn" disabled={page <= 1} onClick={() => gotoPage(page - 1)}>
                Prev
              </button>
              <span>
                Page {page} of {lastPage}
              </span>
              <button
                className="btn"
                disabled={page >= lastPage}
                onClick={() => gotoPage(page + 1)}
              >
                Next
              </button>
              <button
                className="btn"
                disabled={page >= lastPage}
                onClick={() => gotoPage(lastPage)}
              >
                &raquo;
              </button>
              <label style={{ marginLeft: 12, fontSize: 13 }}>
                Rows per page:&nbsp;
                <input
                  type="number"
                  min={1}
                  max={500}
                  value={limitInput}
                  style={{ width: 60, fontSize: 13, padding: "2px 4px" }}
                  onChange={e => setLimitInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') handleLimitUpdate();
                  }}
                  onBlur={handleLimitUpdate}
                />
              </label>
            </div>
          </div>

          {/* Bottom Attachments Pane */}
          <div>
            {bottomPaneMode === 'attachments' && attachmentsClientId && (
              <div className="client-attachments-panel">
                <div className="bottom-pane-toolbar">
                  <button className={`btn toggle ${bottomPaneMode === 'attachments' ? 'active' : ''}`} onClick={() => setBottomPaneMode('attachments')} aria-pressed={bottomPaneMode === 'attachments'} title="Attachments">
                    <FaPaperclip style={{ verticalAlign: '-2px' }} />
                    <span style={{ marginLeft: 6 }}>Attachments</span>
                  </button>
                  <div style={{ flex: 1 }} />
                  <button className="btn" onClick={() => setBottomPaneMode(null)} title="Close">
                    <FaTimes style={{ verticalAlign: '-2px', marginRight: 6 }} /> Close
                  </button>
                </div>

                <h2>Attachments for Client {attachmentsClientName ? `“${attachmentsClientName}”` : `#${attachmentsClientId}`}</h2>

                <div className="attachments-toolbar">
                  <div className={`dropzone ${clientIsDragging ? 'dragging' : ''}`} onDragOver={onClientDragOver} onDragLeave={onClientDragLeave} onDrop={onClientDrop} title="Drop files here">
                    Drop files here
                  </div>
                  <div className="attach-actions">
                    <input type="text" className="attach-desc" placeholder="Description (optional)" value={clientAttachDesc} onChange={e => setClientAttachDesc(e.target.value)} />
                    <input type="file" multiple ref={clientFileInputRef} style={{ display: 'none' }} onChange={e => { const files = Array.from(e.target.files || []); if (files.length) setClientPendingFiles(files); if (clientFileInputRef.current) clientFileInputRef.current.value = ''; }} />
                    <button className="btn" onClick={() => clientFileInputRef.current?.click()}><FaFolderOpen style={{ verticalAlign: '-2px' }} /> Browse…</button>
                    <button className="btn" onClick={() => fetchClientAttachments(attachmentsClientId)} title="Refresh"><FaSync style={{ verticalAlign: '-2px' }} /></button>
                    <button className="btn primary" disabled={!clientPendingFiles.length} onClick={uploadClientFiles} title={clientPendingFiles.length ? `Upload ${clientPendingFiles.length} file(s)` : 'Choose files first'}><FaUpload style={{ verticalAlign: '-2px' }} />Upload</button>
                  </div>
                </div>

                {!!clientPendingFiles.length && (
                  <div className="pending-files">
                    {clientPendingFiles.map((f, i) => (
                      <span key={f.name + i} className="file-chip">
                        {f.name}
                        <button type="button" className="chip-x" title="Remove" aria-label={`Remove ${f.name}`} onClick={() => removeClientPendingFile(i)}>×</button>
                      </span>
                    ))}
                  </div>
                )}

                <div className="table-scroll-wrapper">
                  <table className="attachments-table" style={{ width: '100%', background: 'transparent' }}>
                    <thead>
                      <tr>
                        <th className="actions" style={{ width: 72 }}>Action</th>
                        <th>Filename</th>
                        <th>Date</th>
                        <th className="desc">Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clientAttachmentsLoading ? (
                        <tr><td colSpan={4}>Loading…</td></tr>
                      ) : clientAttachments.length ? (
                        clientAttachments.map(a => {
                          const name = (a.filename || '').split('/').pop();
                          return (
                            <tr key={a.id ?? name}>
                              <td className="actions">
                                {a.id ? (
                                  <button className="mini-btn danger" title="Delete attachment" onClick={() => deleteClientAttachment(a.id)}>
                                    <FaTrash size={16} />
                                  </button>
                                ) : null}
                              </td>
                              <td><a href={`${API}/api/clients/${attachmentsClientId}/attachments/${encodeURIComponent(name)}`} target="_blank" rel="noopener noreferrer">{name}</a></td>
                              <td>{a.date ? new Date(a.date).toLocaleString() : ''}</td>
                              <td className="desc">{a.description || ''}</td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr><td colSpan={4} style={{ color: '#aaa' }}>No attachments</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </Split>
      )}
    </>
  );

  const renderWarehouseFees = () => (
    <div className="fees-wrapper">
      <div className="fees-toolbar">
        <button className="btn" onClick={addWarehouseFee}>
          <FaPlus /> Add Storage Fee
        </button>
      </div>
      {loadingFees ? (
        <div className="loading">Loading…</div>
      ) : (
        <table className="fees-table">
          <thead>
            <tr>
              <th>Warehouse</th>
              <th>Storage Unit</th>
              <th>Fee</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {warehouseFees.length ? (
              warehouseFees.map((f, idx) => {
                const template = tplWarehouse.find(t => t.whstorageunit === f.whstorageunit) || { name: 'Unknown' };
                return (
                  <tr key={f.id || `n-${idx}`}>
                    <td>
                    <select
                      value={f.warehouseid ?? 1}
                      onChange={(e) => {
                        const next = { ...f, warehouseid: numberOrNull(e.target.value, 1) };
                        updateLocalFee(setWarehouseFees, idx, { warehouseid: next.warehouseid });
                        scheduleFeeAutoSave({ key: `wh-${f.id ?? 'n'+idx}`, fee: next, singlePath: 'fees/warehouse', collectionPath: 'fees/warehouse' });
                      }}
                    >
                        {warehouses.map((w) => (
                          <option key={w.id} value={w.id}>
                            {w.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                    <select
                      value={f.whstorageunit}
                      onChange={(e) => {
                        const whstorageunit = Number(e.target.value);
                        const template = tplWarehouse.find((t) => t.whstorageunit === whstorageunit) || { fee: 0 };
                        const next = { ...f, whstorageunit, fee: template.fee };
                        updateLocalFee(setWarehouseFees, idx, { whstorageunit, fee: template.fee });
                        scheduleFeeAutoSave({ key: `wh-${f.id ?? 'n'+idx}`, fee: next, singlePath: 'fees/warehouse', collectionPath: 'fees/warehouse' });
                      }}
                    >
                        <option value="">— Select —</option>
                        {storageUnitOptions.map((su) => (
                          <option key={su.id} value={su.id}>
                            {su.name || su.id}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                    <input
                      type="text"
                      inputMode="decimal"
                      style={{ width: '12ch' }}
                      value={typeof f._feeStr === 'string' ? f._feeStr : toFixed2(f.fee ?? 0)}
                      onChange={(e) => {
                        const raw = (e.target.value || '').replace(/,/g, '');
                        if (raw === '' || isValidMoneyStr(raw)) {
                          updateLocalFee(setWarehouseFees, idx, { _feeStr: raw });
                        }
                      }}
                      onBlur={(e) => {
                        const raw = (typeof f._feeStr === 'string' ? f._feeStr : e.target.value || '').replace(/,/g, '');
                        const nextFee = round2(raw === '' ? f.fee ?? 0 : raw);
                        const next = { ...f, fee: nextFee, _feeStr: undefined };
                        updateLocalFee(setWarehouseFees, idx, { fee: nextFee, _feeStr: undefined });
                        scheduleFeeAutoSave({ key: `wh-${f.id ?? 'n'+idx}`, fee: next, singlePath: 'fees/warehouse', collectionPath: 'fees/warehouse' });
                      }}
                    />
                    </td>
                  <td className="actions">
                      <button
                        className="mini-btn danger"
                        title="Delete Fee"
                        onClick={() =>
                          deleteFee({ fee: f, singlePath: 'fees/warehouse' })
                        }
                        disabled={savingFee}
                      >
                        <FaTrash size={18}/>
                      </button>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={4}>No fees</td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );

  const renderReceiptFees = () => (
    <div className="fees-wrapper">
      <div className="fees-toolbar receiving">
        <button className="btn" onClick={addReceiptFee}>
          <FaPlus /> Add Receiving Fee
        </button>
      </div>
      {loadingFees ? (
        <div className="loading">Loading…</div>
      ) : (
        <table className="fees-table">
          <thead>
            <tr>
              <th>Warehouse</th>
              <th>Fee Name</th>
              <th>Amt</th>
              <th title="Auto Charge ensures this charge will automatically be added to all new Warehouse Receipts.">Auto Charge</th>
              <th title="xPallets will take the charge specified and multiply it by the number of pallets on the receipt.">xPallets</th>
              <th title="Use Vol will use volume as the basis for charging">Use Vol</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {receiptFees.length ? (
              receiptFees.map((f, idx) => (
                <tr key={f.id || `n-${idx}`}>
                  <td>
                    <select
                      value={f.warehouseid ?? 1}
                      onChange={(e) => {
                        const next = { ...f, warehouseid: numberOrNull(e.target.value, 1) };
                        updateLocalFee(setReceiptFees, idx, { warehouseid: next.warehouseid });
                        scheduleFeeAutoSave({ key: `rf-${f.id ?? 'n'+idx}`, fee: next, singlePath: 'fees/receipt', collectionPath: 'fees/receipt' });
                      }}
                    >
                      {warehouses.map((w) => (
                        <option key={w.id} value={w.id}>
                          {w.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    {(() => {
                      const currTpl = tplReceipt.find((t) => String(t.name) === String(f.name));
                      const tip = (currTpl?.description || f.description || '').toString();
                      return (
                        <select
                          value={f.name ?? ''}
                          title={tip}
                          onChange={(e) => {
                            const picked = tplReceipt.find((t) => String(t.name) === String(e.target.value));
                            let next = { ...f, name: e.target.value };
                            if (picked) next = applyReceiptTemplateValues(next, picked);
                            updateLocalFee(setReceiptFees, idx, next);
                            scheduleFeeAutoSave({ key: `rf-${f.id ?? 'n'+idx}`, fee: next, singlePath: 'fees/receipt', collectionPath: 'fees/receipt' });
                          }}
                        >
                          <option value="">— Select —</option>
                          {tplReceipt.map((t) => (
                            <option key={t.id} value={t.name} title={t.description || ''}>
                              {t.name}
                            </option>
                          ))}
                        </select>
                      );
                    })()}
                  </td>
                  <td>
                    <input
                      type="text"
                      inputMode="decimal"
                      style={{ width: '12ch' }}
                      value={typeof f._amtStr === 'string' ? f._amtStr : toFixed2(f.amt ?? 0)}
                      onChange={(e) => {
                        const raw = (e.target.value || '').replace(/,/g, '');
                        if (raw === '' || isValidMoneyStr(raw)) {
                          updateLocalFee(setReceiptFees, idx, { _amtStr: raw });
                        }
                      }}
                      onBlur={(e) => {
                        const raw = (typeof f._amtStr === 'string' ? f._amtStr : e.target.value || '').replace(/,/g, '');
                        const nextAmt = round2(raw === '' ? f.amt ?? 0 : raw);
                        const next = { ...f, amt: nextAmt, _amtStr: undefined };
                        updateLocalFee(setReceiptFees, idx, { amt: nextAmt, _amtStr: undefined });
                        scheduleFeeAutoSave({ key: `rf-${f.id ?? 'n'+idx}`, fee: next, singlePath: 'fees/receipt', collectionPath: 'fees/receipt' });
                      }}
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={Number(f.auto || 0) === 1}
                      onChange={(e) => {
                        const v = e.target.checked ? 1 : 0;
                        const next = { ...f, auto: v };
                        updateLocalFee(setReceiptFees, idx, { auto: v });
                        scheduleFeeAutoSave({ key: `rf-${f.id ?? 'n'+idx}`, fee: next, singlePath: 'fees/receipt', collectionPath: 'fees/receipt' });
                      }}
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={Number(f.xpallets || 0) === 1}
                      onChange={(e) => {
                        const v = e.target.checked ? 1 : 0;
                        const next = { ...f, xpallets: v };
                        updateLocalFee(setReceiptFees, idx, { xpallets: v });
                        scheduleFeeAutoSave({ key: `rf-${f.id ?? 'n'+idx}`, fee: next, singlePath: 'fees/receipt', collectionPath: 'fees/receipt' });
                      }}
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={Number(f.usevolume || 0) === 1}
                      onChange={(e) => {
                        const v = e.target.checked ? 1 : 0;
                        const next = { ...f, usevolume: v };
                        updateLocalFee(setReceiptFees, idx, { usevolume: v });
                        scheduleFeeAutoSave({ key: `rf-${f.id ?? 'n'+idx}`, fee: next, singlePath: 'fees/receipt', collectionPath: 'fees/receipt' });
                      }}
                    />
                  </td>
                  <td className="actions">
                    <button
                      className="mini-btn danger"
                      title="Delete Fee"
                      onClick={() =>
                        deleteFee({ fee: f, singlePath: 'fees/receipt' })
                      }
                      disabled={savingFee}
                    >
                      <FaTrash size={18}/>
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7}>No fees</td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );

  const renderCustomFees = () => (
    <div className="fees-wrapper">
      <div className="fees-toolbar custom">
        <button className="btn" onClick={addCustomFee}>
          <FaPlus /> Add Custom Fee
        </button>
      </div>
      {loadingFees ? (
        <div className="loading">Loading…</div>
      ) : (
        <table className="fees-table">
          <thead>
            <tr>
              <th>Warehouse</th>
              <th>Fee Name</th>
              <th>Amt</th>
              <th title="Auto Charge ensures this charge will automatically be added to all new Warehouse Receipts.">Auto Charge</th>
              <th title="xPallets will take the charge specified and multiply it by the number of pallets on the receipt.">xPallets</th>
              <th title="Use Vol will use volume as the basis for charging">Use Vol</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {customFees.length ? (
              customFees.map((f, idx) => (
                <tr key={f.id || `n-${idx}`}>
                  <td>
                    <select
                      value={f.warehouseid ?? 1}
                      onChange={(e) => {
                        const next = { ...f, warehouseid: numberOrNull(e.target.value, 1) };
                        updateLocalFee(setCustomFees, idx, { warehouseid: next.warehouseid });
                        scheduleFeeAutoSave({ key: `cf-${f.id ?? 'n'+idx}`, fee: next, singlePath: 'fees/custom', collectionPath: 'fees/custom' });
                      }}
                    >
                      {warehouses.map((w) => (
                        <option key={w.id} value={w.id}>
                          {w.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    {(() => {
                      const currTpl = tplCustom.find((t) => String(t.name) === String(f.name));
                      const tip = (currTpl?.description || f.description || '').toString();
                      return (
                        <select
                          value={f.name ?? ''}
                          title={tip}
                          onChange={(e) => {
                            const picked = tplCustom.find((t) => String(t.name) === String(e.target.value));
                            let next = { ...f, name: e.target.value };
                            if (picked) next = applyCustomTemplateValues(next, picked);
                            updateLocalFee(setCustomFees, idx, next);
                            scheduleFeeAutoSave({ key: `cf-${f.id ?? 'n'+idx}`, fee: next, singlePath: 'fees/custom', collectionPath: 'fees/custom' });
                          }}
                        >
                          <option value="">— Select —</option>
                          {tplCustom.map((t) => (
                            <option key={t.id} value={t.name} title={t.description || ''}>
                              {t.name}
                            </option>
                          ))}
                        </select>
                      );
                    })()}
                  </td>
                  <td>
                    <input
                      type="text"
                      inputMode="decimal"
                      style={{ width: '12ch' }}
                      value={typeof f._amtStr === 'string' ? f._amtStr : toFixed2(f.amt ?? 0)}
                      onChange={(e) => {
                        const raw = (e.target.value || '').replace(/,/g, '');
                        if (raw === '' || isValidMoneyStr(raw)) {
                          updateLocalFee(setCustomFees, idx, { _amtStr: raw });
                        }
                      }}
                      onBlur={(e) => {
                        const raw = (typeof f._amtStr === 'string' ? f._amtStr : e.target.value || '').replace(/,/g, '');
                        const nextAmt = round2(raw === '' ? f.amt ?? 0 : raw);
                        const next = { ...f, amt: nextAmt, _amtStr: undefined };
                        updateLocalFee(setCustomFees, idx, { amt: nextAmt, _amtStr: undefined });
                        scheduleFeeAutoSave({ key: `cf-${f.id ?? 'n'+idx}`, fee: next, singlePath: 'fees/custom', collectionPath: 'fees/custom' });
                      }}
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={Number(f.auto || 0) === 1}
                      onChange={(e) => {
                        const v = e.target.checked ? 1 : 0;
                        const next = { ...f, auto: v };
                        updateLocalFee(setCustomFees, idx, { auto: v });
                        scheduleFeeAutoSave({ key: `cf-${f.id ?? 'n'+idx}`, fee: next, singlePath: 'fees/custom', collectionPath: 'fees/custom' });
                      }}
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={Number(f.xpallets || 0) === 1}
                      onChange={(e) => {
                        const v = e.target.checked ? 1 : 0;
                        const next = { ...f, xpallets: v };
                        updateLocalFee(setCustomFees, idx, { xpallets: v });
                        scheduleFeeAutoSave({ key: `cf-${f.id ?? 'n'+idx}`, fee: next, singlePath: 'fees/custom', collectionPath: 'fees/custom' });
                      }}
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={Number(f.usevolume || 0) === 1}
                      onChange={(e) => {
                        const v = e.target.checked ? 1 : 0;
                        const next = { ...f, usevolume: v };
                        updateLocalFee(setCustomFees, idx, { usevolume: v });
                        scheduleFeeAutoSave({ key: `cf-${f.id ?? 'n'+idx}`, fee: next, singlePath: 'fees/custom', collectionPath: 'fees/custom' });
                      }}
                    />
                  </td>
                  <td className="actions">
                    <button
                      className="mini-btn danger"
                      title="Delete Fee"
                      onClick={() =>
                        deleteFee({ fee: f, singlePath: 'fees/custom' })
                      }
                      disabled={savingFee}
                    >
                      <FaTrash size={18}/>
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7}>No custom fees</td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );

  const renderOrderFees = () => (
    <div className="fees-wrapper">
      <div className="fees-toolbar">
        <button className="btn" onClick={addOrderFee}>
          <FaPlus /> Add Order Fee
        </button>
      </div>
      {loadingFees ? (
        <div className="loading">Loading…</div>
      ) : (
        <table className="fees-table">
          <thead>
            <tr>
              <th>Warehouse</th>
              <th>Fee Name</th>
              <th>Amt</th>
              <th>Auto</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {orderFees.length ? (
              orderFees.map((f, idx) => (
                <tr key={f.id || `n-${idx}`}>
                  <td>
                    <select
                      value={f.warehouseid ?? 1}
                      onChange={(e) => {
                        const next = { ...f, warehouseid: numberOrNull(e.target.value, 1) };
                        updateLocalFee(setOrderFees, idx, { warehouseid: next.warehouseid });
                        scheduleFeeAutoSave({ key: `of-${f.id ?? 'n'+idx}`, fee: next, singlePath: 'fees/order', collectionPath: 'fees/order' });
                      }}
                    >
                      {warehouses.map((w) => (
                        <option key={w.id} value={w.id}>
                          {w.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    {(() => {
                      const isCustom = Number(f.feeid || 0) === 0;
                      const currTpl = tplOrder.find((t) => Number(t.id) === Number(f.feeid));
                      const tip = (isCustom ? (f.feename || 'Custom Fee') : (currTpl?.description || f.description || '')).toString();
                      const onPick = (e) => {
                        const feeid = Number(e.target.value);
                        if (!feeid) {
                          // custom selected: set feeid=0 and prompt for name
                          updateLocalFee(setOrderFees, idx, { feeid: 0 });
                          setCustomOrderFee({ open: true, index: idx, value: f.feename || '' });
                          return;
                        }
                        const template = tplOrder.find((t) => Number(t.id) === feeid);
                        const next = applyOrderTemplateValues({ ...f, feeid }, template);
                        updateLocalFee(setOrderFees, idx, next);
                        scheduleFeeAutoSave({ key: `of-${f.id ?? 'n'+idx}`, fee: next, singlePath: 'fees/order', collectionPath: 'fees/order' });
                      };
                      return (
                        <span style={{ display:'inline-flex', alignItems:'center', gap:6 }}>
                          <select
                            value={Number(f.feeid ?? 0)}
                            title={tip}
                            onChange={onPick}
                            style={{ minWidth: 200 }}
                          >
                            <option value={0} title={f.feename || 'Custom fee name'}>
                              {Number(f.feeid || 0) === 0 ? (f.feename || '- Custom -') : '- Custom -'}
                            </option>
                            {tplOrder.map((t) => (
                              <option key={t.id} value={t.id} title={t.description || ''}>
                                {t.feename}
                              </option>
                            ))}
                          </select>
                          {isCustom ? (
                            <button
                              type="button"
                              className="mini-btn"
                              title="Rename custom fee"
                              aria-label="Rename custom fee"
                              onClick={() => setCustomOrderFee({ open: true, index: idx, value: f.feename || '' })}
                              style={{ display:'inline-flex', alignItems:'center', justifyContent:'center' }}
                            >
                              <FaEdit size={16} />
                            </button>
                          ) : null}
                        </span>
                      );
                    })()}
                  </td>
                  
                  <td>
                    <input
                      type="text"
                      inputMode="decimal"
                      style={{ width: '12ch' }}
                      value={typeof f._amtStr === 'string' ? f._amtStr : toFixed2(f.amt ?? 0)}
                      onChange={(e) => {
                        const raw = (e.target.value || '').replace(/,/g, '');
                        if (raw === '' || isValidMoneyStr(raw)) {
                          updateLocalFee(setOrderFees, idx, { _amtStr: raw });
                        }
                      }}
                      onBlur={(e) => {
                        const raw = (typeof f._amtStr === 'string' ? f._amtStr : e.target.value || '').replace(/,/g, '');
                        const nextAmt = round2(raw === '' ? f.amt ?? 0 : raw);
                        const next = { ...f, amt: nextAmt, _amtStr: undefined };
                        updateLocalFee(setOrderFees, idx, { amt: nextAmt, _amtStr: undefined });
                        scheduleFeeAutoSave({ key: `of-${f.id ?? 'n'+idx}`, fee: next, singlePath: 'fees/order', collectionPath: 'fees/order' });
                      }}
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={Number(f.auto || 0) === 1}
                      onChange={(e) => {
                        const v = e.target.checked ? 1 : 0;
                        const next = { ...f, auto: v };
                        updateLocalFee(setOrderFees, idx, { auto: v });
                        scheduleFeeAutoSave({ key: `of-${f.id ?? 'n'+idx}`, fee: next, singlePath: 'fees/order', collectionPath: 'fees/order' });
                      }}
                    />
                  </td>
                  <td className="actions">
                    <button
                      className="mini-btn danger"
                      title="Delete Fee"
                      onClick={() =>
                        deleteFee({ fee: f, singlePath: 'fees/order' })
                      }
                      disabled={savingFee}
                    >
                      <FaTrash size={18}/>
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6}>No fees</td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );

  // NEW: eOrder fees (clienteorderfees) — behaves like Order Fees, sourced from template_eorderfees
  const renderEOrderFees = () => (
    <div className="fees-wrapper">
      <div className="fees-toolbar">
        <button className="btn" onClick={addEOrderClientFee}>
          <FaPlus /> Add E-Order Fee
        </button>
      </div>
      {loadingFees ? (
        <div className="loading">Loading…</div>
      ) : (
        <table className="fees-table">
          <thead>
            <tr>
              <th>Warehouse</th>
              <th>Fee Name</th>
              <th>Amt</th>
              <th>Fixed Qty</th>
              <th>Auto</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {eorderClientFees.length ? (
              eorderClientFees.map((f, idx) => (
                <tr key={f.id || `n-${idx}`}>
                  <td>
                    <select
                      value={f.warehouseid ?? 1}
                      onChange={(e) =>
                        updateLocalFee(setEorderClientFees, idx, {
                          warehouseid: numberOrNull(e.target.value, 1),
                        })
                      }
                    >
                      {warehouses.map((w) => (
                        <option key={w.id} value={w.id}>
                          {w.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    {(() => {
                      const currTpl = tplEOrder.find((t) => String(t.feename || '') === String(f.feename || ''));
                      const isCustom = !currTpl; // if not matching template, it's custom
                      const tip = (isCustom ? (f.feename || 'Custom Fee') : (currTpl?.description || '')).toString();
                      const selectedVal = isCustom ? 0 : Number(currTpl?.id);
                      const onPick = (e) => {
                        const val = Number(e.target.value);
                        if (!val) {
                          setCustomEOrderFee({ open: true, index: idx, value: f.feename || '' });
                          return;
                        }
                        const t = tplEOrder.find((x) => Number(x.id) === val);
                        if (t) {
                          const next = { ...f, feename: t.feename || '', description: t.description || '', amt: t.amt ?? 0, auto: Number(t.auto || 0) };
                          updateLocalFee(setEorderClientFees, idx, { feename: next.feename, description: next.description, amt: next.amt, auto: next.auto });
                          scheduleFeeAutoSave({ key: `eof-${f.id ?? 'n'+idx}`, fee: next, singlePath: 'fees/eorder', collectionPath: 'fees/eorder' });
                        }
                      };
                      return (
                        <span style={{ display:'inline-flex', alignItems:'center', gap:6 }}>
                          <select value={selectedVal} title={tip} onChange={onPick} style={{ minWidth: 200 }}>
                            <option value={0} title={f.feename || 'Custom fee name'}>
                              {isCustom ? (f.feename || '- Custom -') : '- Custom -'}
                            </option>
                            {tplEOrder.map((t) => (
                              <option key={t.id} value={t.id} title={t.description || ''}>
                                {t.feename}
                              </option>
                            ))}
                          </select>
                          {isCustom ? (
                            <button
                              type="button"
                              className="mini-btn"
                              title="Rename custom fee"
                              aria-label="Rename custom fee"
                              onClick={() => setCustomEOrderFee({ open: true, index: idx, value: f.feename || '' })}
                              style={{ display:'inline-flex', alignItems:'center', justifyContent:'center' }}
                            >
                              <FaEdit size={16} />
                            </button>
                          ) : null}
                        </span>
                      );
                    })()}
                  </td>
                  <td>
                    <input
                      type="text"
                      inputMode="decimal"
                      style={{ width: '12ch' }}
                      value={typeof f._amtStr === 'string' ? f._amtStr : toFixed2(f.amt ?? 0)}
                      onChange={(e) => {
                        const raw = (e.target.value || '').replace(/,/g, '');
                        if (raw === '' || isValidMoneyStr(raw)) {
                          updateLocalFee(setEorderClientFees, idx, { _amtStr: raw });
                        }
                      }}
                      onBlur={(e) => {
                        const raw = (typeof f._amtStr === 'string' ? f._amtStr : e.target.value || '').replace(/,/g, '');
                        const nextAmt = round2(raw === '' ? f.amt ?? 0 : raw);
                        const next = { ...f, amt: nextAmt, _amtStr: undefined };
                        updateLocalFee(setEorderClientFees, idx, { amt: nextAmt, _amtStr: undefined });
                        scheduleFeeAutoSave({ key: `eof-${f.id ?? 'n'+idx}`, fee: next, singlePath: 'fees/eorder', collectionPath: 'fees/eorder' });
                      }}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      min={0}
                      step={1}
                      style={{ width: '8ch' }}
                      value={Number.isFinite(Number(f.fixedqty)) ? String(f.fixedqty) : '0'}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (/^\d*$/.test(v)) {
                          const n = v === '' ? '' : Math.max(0, parseInt(v || '0', 10));
                          updateLocalFee(setEorderClientFees, idx, { fixedqty: n === '' ? 0 : n });
                          const next = { ...f, fixedqty: n === '' ? 0 : n };
                          scheduleFeeAutoSave({ key: `eof-${f.id ?? 'n'+idx}`, fee: next, singlePath: 'fees/eorder', collectionPath: 'fees/eorder' });
                        }
                      }}
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={Number(f.auto || 0) === 1}
                      onChange={(e) => {
                        const v = e.target.checked ? 1 : 0;
                        const next = { ...f, auto: v };
                        updateLocalFee(setEorderClientFees, idx, { auto: v });
                          scheduleFeeAutoSave({ key: `eof-${f.id ?? 'n'+idx}`, fee: next, singlePath: 'fees/eorder', collectionPath: 'fees/eorder' });
                      }}
                    />
                  </td>
                  <td className="actions">
                    <button
                      className="mini-btn danger"
                      title="Delete Fee"
                      onClick={() =>
                        deleteFee({ fee: f, singlePath: 'fees/eorder' })}
                      disabled={savingFee}
                    >
                      <FaTrash size={18} />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6}>No fees</td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );

  const renderClientForm = () => {
    if (!selected)
      return <div className="client-editor empty">Select a client or click “New Client”.</div>;
    const setField = (k, v) => setSelected((s) => ({ ...s, [k]: v }));

    return (
      <div className="client-editor">
        <div className="tabs">
          <button onClick={() => setTab('client')}        className={tab === 'client' ? 'active' : ''}>Client</button>
          <button onClick={() => setTab('warehousefees')} className={tab === 'warehousefees' ? 'active' : ''}>Storage Fees</button>
          <button onClick={() => setTab('receiptfees')}   className={tab === 'receiptfees' ? 'active' : ''}>Receiving Fees</button>
          <button onClick={() => setTab('orderfees')}     className={tab === 'orderfees' ? 'active' : ''}>Order Fees</button>
          <button onClick={() => setTab('eorderfees')}    className={tab === 'eorderfees' ? 'active' : ''}>eOrder Fees</button>
          {isAdmin && (
            <button onClick={() => setTab('shipping')}    className={tab === 'shipping' ? 'active' : ''}>Shipping</button>
          )}
          <button onClick={() => setTab('customfees')}  className={tab === 'customfees' ? 'active' : ''}>Custom Fees</button>
        </div>
        {tab === 'client'        && (
          <div className="client-form">
            <div className="form-grid">
              <label>
                Name
                <input value={selected.name || ""} onChange={e => setField('name', e.target.value)} />
              </label>
              <label>
                Address
                <input value={selected.address || ""} onChange={e => setField('address', e.target.value)} />
              </label>
              <label>
                City
                <input value={selected.city || ""} onChange={e => setField('city', e.target.value)} />
              </label>
              <label>
                State
                <select value={selected.state || ""} onChange={e => setField('state', e.target.value)}>
                  <option value="">Select a state</option>
                  {US_STATES.map((state) => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </label>
              <label>
                Zip
                <input value={selected.zip || ""} onChange={e => setField('zip', e.target.value)} />
              </label>
              <label>
                Country
                <input value={selected.country || ""} onChange={e => setField('country', e.target.value)} />
              </label>
              <label>
                Main Telephone
                <input value={selected.telephone || ""} onChange={e => setField('telephone', e.target.value)} />
              </label>
              <label>
                Main Contact
                <input value={selected.contact || ""} onChange={e => setField('contact', e.target.value)} />
              </label>
              <label>
                Main Email
                <input value={selected.email || ""} onChange={e => setField('email', e.target.value)} />
              </label>
              <label>
                Minimum Monthly Storage ($)
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={selected.monthlystorageminimum ?? ""}
                  onChange={e => setField('monthlystorageminimum', numberOrNull(e.target.value, 0))}
                />
              </label>
              <label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span>Storage Invoicing Calculation</span>
                  <select
                    value={selected.monthlystoragebillingfreq ?? ""}
                    onChange={e => setField('monthlystoragebillingfreq', Number(e.target.value))}
                    style={{ flex: '0 1 220px' }}
                  >
                    <option value={1}>Monthly</option>
                    <option value={3}>Weekly</option>
                    <option value={2}>Daily</option>
                  </select>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <input
                      id="autogen-storage"
                      type="checkbox"
                      checked={Boolean(selected.autogenerate_storage)}
                      onChange={e => setField('autogenerate_storage', e.target.checked ? 1 : 0)}
                    />
                    <label htmlFor="autogen-storage" style={{ margin: 0 }}>Generate auto</label>
                    <span>Every Friday After 5pm</span>
                  </div>
                </div>
              </label>
              <label>
                Storage fees are applied per
                <select
                  value={selected.monthlystoragetype ?? ""}
                  onChange={e => setField('monthlystoragetype', Number(e.target.value))}
                >
                  <option value={0}>Month</option>
                  <option value={1}>Day</option>
                </select>
              </label>
              <label>
                Storage Charge Type
                <select
                  value={selected.storagefeeper ?? ""}
                  onChange={e => setField('storagefeeper', Number(e.target.value))}
                >
                  <option value={2}>By Pallet</option>
                  <option value={3}>By Volume</option>
                  <option value={4}>By Item Volume</option>
                </select>
              </label>
              <label>
                Billing Telephone
                <input value={selected.billingtelephone || ""} onChange={e => setField('billingtelephone', e.target.value)} />
              </label>
              <label>
                Billing Contact
                <input value={selected.billingcontact || ""} onChange={e => setField('billingcontact', e.target.value)} />
              </label>
              <label>
                Billing Email
                <input value={selected.billingemail || ""} onChange={e => setField('billingemail', e.target.value)} />
              </label>
              <label className="span-2">
                Notes
                <textarea value={selected.notes || ""} onChange={e => setField('notes', e.target.value)} />
              </label>
              <label>
                Payment Terms
                <select
                  value={selected.paymentterms ?? ""}
                  onChange={e => setField('paymentterms', Number(e.target.value))}
                >
                  <option value={0}>On Receipt</option>
                  <option value={30}>30 Days</option>
                  <option value={60}>60 Days</option>
                </select>
              </label>
              <label className="span-2">
                Logo
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 120, height: 120, border: 1 ? '1px solid #ccc' : 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa' }}>
                    {selected?.id ? (
                      <img
                        src={`${API}/api/clients/${selected.id}/logo?ts=${Date.now()}`}
                        alt="Client Logo"
                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    ) : (
                      <span style={{ opacity: .6, fontSize: 12 }}>No Logo</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={async (e) => {
                        const file = e.target.files && e.target.files[0];
                        if (!file || !selected?.id) return;
                        const reader = new FileReader();
                        reader.onload = async () => {
                          try {
                            const b64 = typeof reader.result === 'string' ? reader.result : '';
                            await fetch(`${API}/api/clients/${selected.id}/logo`, {
                              method: 'PUT',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ logoBase64: b64 })
                            });
                            // Force reload image by updating state
                            setSelected(s => ({ ...s }));
                          } catch {}
                        };
                        reader.readAsDataURL(file);
                      }}
                    />
                    <button
                      type="button"
                      className="btn danger"
                      onClick={async () => {
                        if (!selected?.id) return;
                        try {
                          await fetch(`${API}/api/clients/${selected.id}/logo`, {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ clear: true })
                          });
                          setSelected(s => ({ ...s }));
                        } catch {}
                      }}
                    >
                      Clear Logo
                    </button>
                  </div>
                </div>
              </label>
            </div>
            <div className="actions">
              <button
                className="btn"
                onClick={() => setSelected(null)}
                type="button"
                style={{ marginRight: 10, display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <FaTimes size={18} />
                Close
              </button>
              <button className="btn" onClick={saveClient} disabled={savingClient}>
                <FaRegSave size={18}/> {savingClient ? 'Saving…' : 'Save Client'}
              </button>
            </div>
          </div>
        )}
        {tab === 'warehousefees' && renderWarehouseFees()}
        {tab === 'receiptfees'   && renderReceiptFees()}
        {tab === 'orderfees'     && renderOrderFees()}
        {tab === 'eorderfees'    && renderEOrderFees()}
        {tab === 'shipping'      && (
          <div className="client-form">
            <div className="form-grid">
              <label>
                Shipping Balance ($)
                <input
                  type="text"
                  value={shipBalanceStr}
                  onChange={e => {
                    const v = e.target.value; setShipBalanceStr(v);
                    const n = parseMoney(v);
                    setSelected(s => s ? { ...s, shippingbalance: n } : s);
                  }}
                  onBlur={() => setShipBalanceStr(formatMoney(selected?.shippingbalance ?? 0))}
                  inputMode="decimal"
                />
              </label>
              <label>
                Funding Limit ($)
                <input
                  type="text"
                  value={fundLimitStr}
                  onChange={e => {
                    const v = e.target.value; setFundLimitStr(v);
                    const n = parseMoney(v);
                    setSelected(s => s ? { ...s, shippingfundlimit: n } : s);
                  }}
                  onBlur={() => setFundLimitStr(formatMoney(selected?.shippingfundlimit ?? 0))}
                  inputMode="decimal"
                />
              </label>
              <label>
                Funding Min Threshold ($)
                <input
                  type="text"
                  value={fundMinStr}
                  onChange={e => {
                    const v = e.target.value; setFundMinStr(v);
                    const n = parseMoney(v);
                    setSelected(s => s ? { ...s, shippingfundminthreshold: n } : s);
                  }}
                  onBlur={() => setFundMinStr(formatMoney(selected?.shippingfundminthreshold ?? 0))}
                  inputMode="decimal"
                />
              </label>
            </div>

            <div className="fees-wrapper" style={{ marginTop: 16 }}>
              <div className="fees-toolbar">
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <select
                    value={shipNewIntegrationId}
                    onChange={e => setShipNewIntegrationId(e.target.value)}
                    className="neo-input"
                    style={{ minWidth: 220 }}
                  >
                    <option value="">Select provider…</option>
                    {shippingProviders
                      .filter(p => !shippingProfiles.some(sp => sp.shipping_integration_id === p.id))
                      .map(p => (
                        <option key={p.id} value={p.id}>
                          {p.displayname || p.servicetype} ({p.servicetype})
                        </option>
                      ))}
                  </select>
                  <button className="btn" onClick={addShippingProfile} disabled={!shipNewIntegrationId}>
                    <FaPlus /> Add Shipping Profile
                  </button>
                </div>
              </div>

              <table className="fees-table">
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>Provider</th>
                    <th>3PL Markup (1–100%)</th>
                  </tr>
                </thead>
                <tbody>
                  {shippingProfiles.length ? (
                    shippingProfiles.map((sp, idx) => (
                      <tr key={sp.id || `sp-${idx}`}>
                        <td style={{ textAlign: 'left' }}>
                          {sp.displayname || sp.servicetype || `#${sp.shipping_integration_id}`}
                        </td>
                        <td>
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <input
                              type="number"
                              inputMode="numeric"
                              step={1}
                              min={1}
                              max={100}
                              onKeyDown={(e) => { if (['e','E','+','-','.'].includes(e.key)) e.preventDefault(); }}
                              style={{ width: 64, textAlign: 'right' }}
                              value={Number.isFinite(Number(sp.markupPct)) ? Math.max(1, Math.min(100, Math.trunc(Number(sp.markupPct)))) : 1}
                              onChange={e => {
                                const raw = e.target.value;
                                let n = parseInt(raw, 10);
                                if (!Number.isFinite(n)) n = 1;
                                n = Math.max(1, Math.min(100, n));
                                setShippingProfiles(prev => prev.map((r, i) => i === idx ? { ...r, markupPct: n } : r));
                              }}
                            />
                            <span style={{ opacity: 0.85 }}>%</span>
                            <div className="actions" style={{ display: 'inline-flex', gap: 6, marginLeft: 6 }}>
                              <button className="mini-btn" title="Save" onClick={() => saveShippingProfile(sp)}>
                                <FaRegSave size={16} />
                              </button>
                              <button className="mini-btn danger" title="Remove" onClick={() => deleteShippingProfile(sp)}>
                                <FaTrash size={16} />
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan={2} style={{ color: '#aaa' }}>No shipping profiles for this client.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="actions">
              <button
                className="btn"
                onClick={() => setSelected(null)}
                type="button"
                style={{ marginRight: 10, display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <FaTimes size={18} />
                Close
              </button>
              <button className="btn" onClick={saveClient} disabled={savingClient}>
                <FaRegSave size={18}/> {savingClient ? 'Saving…' : 'Save Client'}
              </button>
            </div>
          </div>
        )}
        {tab === 'customfees'    && renderCustomFees()}
      </div>
    );
  };

  return (
    <div className="clients-root">
      {renderList()}

      {/* Edit/New Client modal (popup) */}
      {selected && (
        <div className="assign-modal-overlay">
          <div className="client-edit-modal">
            <div className="assign-head" style={{ borderBottom: '1px solid var(--border-color, #3e4654)' }}>
              <h3 style={{ margin: 0 }}>{selected?.id ? `Edit Client #${selected.id}` : 'New Client'}</h3>
              <button className="mini-btn" onClick={() => setSelected(null)} aria-label="Close">×</button>
            </div>
            <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex' }}>
              {renderClientForm()}
            </div>
          </div>
        </div>
      )}
      {/* Confirm dialog */}
      {confirmState.open && (
        <div className="assign-modal-overlay" onClick={closeConfirm}>
          <div className="assign-modal" onClick={(e) => e.stopPropagation()}>
            <div className="assign-head">
              <h3>{confirmState.title}</h3>
              <button className="mini-btn" onClick={closeConfirm} aria-label="Close">×</button>
            </div>
            <div className="assign-search">
              <div style={{ color: 'var(--text-color,#eaeef5)' }}>{confirmState.message}</div>
            </div>
            <div className="assign-actions">
              <button className="btn" onClick={closeConfirm}>{confirmState.cancelText}</button>
              <button
                className="btn primary"
                onClick={() => {
                  try { if (typeof confirmState.onConfirm === 'function') confirmState.onConfirm(); }
                  finally { closeConfirm(); }
                }}
              >
                {confirmState.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notice modal */}
      {notify.open && (
        <div className="assign-modal-overlay" onClick={closeNotify}>
          <div className="assign-modal" onClick={(e) => e.stopPropagation()}>
            <div className="assign-head" style={{ borderBottom: '1px solid var(--border-color, #444)' }}>
              <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 999, background: notify.type === 'error' ? '#ef4444' : notify.type === 'warn' ? '#f59e0b' : '#21bb50' }} />
                {notify.title || (notify.type === 'error' ? 'Something went wrong' : 'Success')}
              </h3>
              <button className="mini-btn" onClick={closeNotify} aria-label="Close">×</button>
            </div>
            <div className="assign-search" style={{ paddingTop: 6 }}>
              <div style={{ color: 'var(--text-color,#eaeef5)' }}>{notify.message}</div>
            </div>
            <div className="assign-actions">
              <button className="btn primary" onClick={closeNotify}>OK</button>
            </div>
          </div>
        </div>
      )}
      {/* Help modal for fee explanations */}
      {help.open && (
        <div className="assign-modal-overlay" onClick={closeHelp}>
          <div className="assign-modal" onClick={(e) => e.stopPropagation()}>
            <div className="assign-head" style={{ borderBottom: '1px solid var(--border-color, #444)' }}>
              <h3>{help.title}</h3>
              <button className="mini-btn" onClick={closeHelp} aria-label="Close">×</button>
            </div>
            <div className="assign-search" style={{ paddingTop: 6 }}>
              <div style={{ color: 'var(--text-color,#eaeef5)' }}>{help.body}</div>
            </div>
            <div className="assign-actions">
              <button className="btn primary" onClick={closeHelp}>OK</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Receiving Fee modal (template picker) */}
      {receiptTemplatePicker.open && (
        <div className="assign-modal-overlay" onClick={closeReceiptTemplatePicker}>
          <div className="assign-modal" onClick={(e) => e.stopPropagation()}>
            <div className="assign-head" style={{ borderBottom: '1px solid var(--border-color, #444)' }}>
              <h3>Add Receiving Fee</h3>
              <button className="mini-btn" onClick={closeReceiptTemplatePicker} aria-label="Close">×</button>
            </div>
            <div className="assign-search" style={{ paddingTop: 6, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span style={{ color: 'var(--text-color,#eaeef5)' }}>Select a template</span>
                <select
                  className="neo-input"
                  value={receiptTemplatePicker.templateId}
                  onChange={(e) => setReceiptTemplatePicker((s) => ({ ...s, templateId: e.target.value }))}
                >
                  {tplReceipt.map((tpl) => (
                    <option key={receiptTemplateValue(tpl)} value={receiptTemplateValue(tpl)}>
                      {tpl.name}{Number.isFinite(Number(tpl.amt)) ? ` (${fmtMoney(tpl.amt)})` : ''}
                    </option>
                  ))}
                </select>
              </label>
              {selectedReceiptTemplate && (
                <div className="template-preview" style={{ fontSize: 14, lineHeight: 1.4 }}>
                  <div><strong>Description:</strong> {selectedReceiptTemplate.description || '—'}</div>
                  <div><strong>Auto charge:</strong> {Number(selectedReceiptTemplate.auto || 0) ? 'Yes' : 'No'}</div>
                  <div><strong>xPallets:</strong> {Number(selectedReceiptTemplate.xpallets || 0) ? 'Yes' : 'No'}</div>
                  <div><strong>Use Volume:</strong> {Number(selectedReceiptTemplate.usevolume || 0) ? 'Yes' : 'No'}</div>
                </div>
              )}
            </div>
            <div className="assign-actions">
              <button className="btn" onClick={closeReceiptTemplatePicker}>Cancel</button>
              <button className="btn primary" onClick={confirmAddReceiptFee}>Add Fee</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Order Fee modal */}
      {orderTemplatePicker.open && (
        <div className="assign-modal-overlay" onClick={closeOrderTemplatePicker}>
          <div className="assign-modal" onClick={(e) => e.stopPropagation()}>
            <div className="assign-head" style={{ borderBottom: '1px solid var(--border-color, #444)' }}>
              <h3>Add Order Fee</h3>
              <button className="mini-btn" onClick={closeOrderTemplatePicker} aria-label="Close">×</button>
            </div>
            <div className="assign-search" style={{ paddingTop: 6, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span style={{ color: 'var(--text-color,#eaeef5)' }}>Select a template</span>
                <select
                  className="neo-input"
                  value={orderTemplatePicker.templateId}
                  onChange={(e) => setOrderTemplatePicker((s) => ({ ...s, templateId: e.target.value }))}
                >
                  {tplOrder.map((tpl) => (
                    <option key={orderTemplateValue(tpl)} value={orderTemplateValue(tpl)}>
                      {tpl.feename}{Number.isFinite(Number(tpl.amt)) ? ` (${fmtMoney(tpl.amt)})` : ''}
                    </option>
                  ))}
                </select>
              </label>
              {selectedOrderTemplate && (
                <div className="template-preview" style={{ fontSize: 14, lineHeight: 1.4 }}>
                  <div><strong>Description:</strong> {selectedOrderTemplate.description || '—'}</div>
                  <div><strong>Auto charge:</strong> {Number(selectedOrderTemplate.auto || 0) ? 'Yes' : 'No'}</div>
                </div>
              )}
            </div>
            <div className="assign-actions">
              <button className="btn" onClick={closeOrderTemplatePicker}>Cancel</button>
              <button className="btn primary" onClick={confirmAddOrderFee}>Add Fee</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Custom Fee modal */}
      {customTemplatePicker.open && (
        <div className="assign-modal-overlay" onClick={closeCustomTemplatePicker}>
          <div className="assign-modal" onClick={(e) => e.stopPropagation()}>
            <div className="assign-head" style={{ borderBottom: '1px solid var(--border-color, #444)' }}>
              <h3>Add Custom Fee</h3>
              <button className="mini-btn" onClick={closeCustomTemplatePicker} aria-label="Close">×</button>
            </div>
            <div className="assign-search" style={{ paddingTop: 6, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span style={{ color: 'var(--text-color,#eaeef5)' }}>Select a template</span>
                <select
                  className="neo-input"
                  value={customTemplatePicker.templateId}
                  onChange={(e) => setCustomTemplatePicker((s) => ({ ...s, templateId: e.target.value }))}
                >
                  {tplCustom.map((tpl) => (
                    <option key={customTemplateValue(tpl)} value={customTemplateValue(tpl)}>
                      {tpl.name}{Number.isFinite(Number(tpl.amt)) ? ` (${fmtMoney(tpl.amt)})` : ''}
                    </option>
                  ))}
                </select>
              </label>
              {selectedCustomTemplate && (
                <div className="template-preview" style={{ fontSize: 14, lineHeight: 1.4 }}>
                  <div><strong>Description:</strong> {selectedCustomTemplate.description || '—'}</div>
                  <div><strong>Auto charge:</strong> {Number(selectedCustomTemplate.auto || 0) ? 'Yes' : 'No'}</div>
                  <div><strong>xPallets:</strong> {Number(selectedCustomTemplate.xpallets || 0) ? 'Yes' : 'No'}</div>
                  <div><strong>Use Volume:</strong> {Number(selectedCustomTemplate.usevolume || 0) ? 'Yes' : 'No'}</div>
                </div>
              )}
            </div>
            <div className="assign-actions">
              <button className="btn" onClick={closeCustomTemplatePicker}>Cancel</button>
              <button className="btn primary" onClick={confirmAddCustomFee}>Add Fee</button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Order Fee Name modal */}
      {customOrderFee.open && (
        <div className="assign-modal-overlay" onClick={closeCustomOrderFee}>
          <div className="assign-modal" onClick={(e) => e.stopPropagation()}>
            <div className="assign-head" style={{ borderBottom: '1px solid var(--border-color, #444)' }}>
              <h3>Custom Order Fee Name</h3>
              <button className="mini-btn" onClick={closeCustomOrderFee} aria-label="Close">×</button>
            </div>
            <div className="assign-search" style={{ paddingTop: 6 }}>
              <div style={{ color: 'var(--text-color,#eaeef5)', marginBottom: 8 }}>Enter a custom fee name for this order fee.</div>
              <input
                className="neo-input"
                value={customOrderFee.value}
                onChange={(e) => setCustomOrderFee(s => ({ ...s, value: e.target.value }))}
                placeholder="e.g., Special Handling"
                autoFocus
              />
            </div>
            <div className="assign-actions">
              <button className="btn" onClick={closeCustomOrderFee}>Cancel</button>
              <button className="btn primary" onClick={saveCustomOrderFee}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Custom E-Order Fee Name modal */}
      {customEOrderFee.open && (
        <div className="assign-modal-overlay" onClick={closeCustomEOrderFee}>
          <div className="assign-modal" onClick={(e) => e.stopPropagation()}>
            <div className="assign-head" style={{ borderBottom: '1px solid var(--border-color, #444)' }}>
              <h3>Custom E-Order Fee Name</h3>
              <button className="mini-btn" onClick={closeCustomEOrderFee} aria-label="Close">×</button>
            </div>
            <div className="assign-search" style={{ paddingTop: 6 }}>
              <div style={{ color: 'var(--text-color,#eaeef5)', marginBottom: 8 }}>Enter a custom fee name for this e-order fee.</div>
              <input
                className="neo-input"
                value={customEOrderFee.value}
                onChange={(e) => setCustomEOrderFee(s => ({ ...s, value: e.target.value }))}
                placeholder="e.g., Special Handling"
                autoFocus
              />
            </div>
            <div className="assign-actions">
              <button className="btn" onClick={closeCustomEOrderFee}>Cancel</button>
              <button className="btn primary" onClick={saveCustomEOrderFee}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
